/**
 * Site Manager Cockpit — shapes the `/api/cockpit/today` envelope into the
 * rows the Today screen renders (mockups/cockpit-v3/Main.dc.html §queue
 * groups + side cards). Pure functions so they are testable without a DOM
 * or a network call, and so fixture mode and the live API produce rows
 * through the same path.
 *
 * The backend contract (azure-functions/cis/shared/cockpit.js `todayView`)
 * does not carry a display id, a formatted "why" caption or a chip colour —
 * those are UI derivations documented inline below.
 */

"use strict";

import { formatPeriodLabel, formatCoversSpan } from "./reportPeriod.js";
import { shapeForecastBand, shapeRepairChecks } from "./planForecast.js";
import { normalisePublishedNumber, reportLabel, reportRef } from "./reportLabel.js";
import { shapeSchedule } from "./reportSchedule.js";
import { CATEGORIES } from "./stageVocabulary.js";

const DAY_MS = 86400000;
const WEEKDAY = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "Mon 24 Aug" from an ISO date/date-time string; the raw value if unparseable. */
export function formatDay(value) {
  const parsed = Date.parse(value);
  if (!Number.isFinite(parsed)) return String(value || "");
  const d = new Date(parsed);
  return `${WEEKDAY[d.getUTCDay()]} ${d.getUTCDate()} ${MONTH[d.getUTCMonth()]}`;
}

/**
 * A raw internal id (clientCaptureId — a UUID v4 per
 * azure-functions/cis/shared/photos.js's validation — or any other bare
 * GUID) is never user-facing (owner live-use defect: "FP1
 * 0592AA22-D1E5-…" on Today rows). Anything else — a yard-file reference,
 * an ITP code, this cockpit's own `SM-<date>-<hex8>` booking ref — passes
 * through unchanged.
 */
const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export function isHumanReadableRef(value) {
  if (!value) return false;
  return !GUID_RE.test(String(value).trim());
}

export function initialsName(upn) {
  const value = String(upn || "").trim();
  if (!value) return "—";
  const namePart = value.includes("@") ? value.split("@")[0] : value;
  const words = namePart.split(/[.\s_-]+/).filter(Boolean);
  if (!words.length) return value;
  if (words.length === 1) return words[0];
  return `${words[0][0].toUpperCase()}. ${words[1][0].toUpperCase()}${words[1].slice(1)}`;
}

/**
 * Owner retest 4: one "Results missing" report row. The readiness item (same
 * response as the report, so always fresh) is authoritative for the fields it
 * carries, including an EMPTY assignedTo, which means Unassigned and must not
 * fall back to another screen's cached row. Fields the item does not carry
 * (older backend) fall back to the Today row matched on project + scheduleRef.
 */
export function mergeMissingResultRow(item, todayRow) {
  const it = item || {};
  const td = todayRow || null;
  if (!td && typeof it.assignedTo !== "string" && typeof it.title !== "string") return null;
  const pick = (key) => (typeof it[key] === "string" ? it[key] : td ? td[key] || "" : "");
  return {
    scheduleRef: it.scheduleRef,
    title: pick("title"),
    plannedDate: pick("plannedDate"),
    category: pick("category"),
    assignedTo: pick("assignedTo"),
  };
}

/**
 * Owner retest 4: "Imported from <file name> on <date>" for a schedule row
 * with an import run reference, or null when it has none. `runsById` maps
 * runId -> { fileName, importedAt } (import history); when the run's file name
 * is not known the line still says when ("Imported on <date>"), never a guess.
 */
export function importProvenanceLabel(sourceRunId, importedAtUtc, runsById) {
  if (!sourceRunId) return null;
  const run = runsById && runsById[sourceRunId];
  const when = (run && run.importedAt) || importedAtUtc || "";
  const day = when ? formatDay(when) : "";
  const file = run && run.fileName;
  if (file) return `Imported from ${file}${day ? ` on ${day}` : ""}`;
  return day ? `Imported on ${day}` : "Imported from a yard schedule";
}

/** Monday (ISO date) of the week containing an ISO date, for "Show on plan". */
export function mondayOfDate(isoDate) {
  const d = new Date(`${String(isoDate).slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return null;
  const dow = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() - (dow - 1));
  return d.toISOString().slice(0, 10);
}

/** Group 1: re-inspections overdue — always the "bad" chip (yard-ready and unbooked). */
export function shapeReinspectionsOverdue(code, rows) {
  return (rows || []).map((row) => ({
    key: `${code}-reinsp-${row.inspectionNumber}`,
    code,
    title: row.title || "",
    subId:
      row.clientCaptureId && isHumanReadableRef(row.clientCaptureId)
        ? String(row.clientCaptureId).toUpperCase()
        : `CI-${row.inspectionNumber}`,
    why: `Our inspection ${formatDay(row.inspectionDate)}`,
    who: initialsName(row.assignedTo),
    chipText: `${row.ageDays}d`,
    chipClass: "bad",
    action: "book",
    scheduleRef: row.scheduleRef || null,
    sourceInspectionNumber: row.inspectionNumber,
  }));
}

/** "3 photos" / "1 photo" / "" — the count only, no separator. */
export function photoCountLabel(photoCount) {
  const n = Number(photoCount || 0);
  if (!n) return "";
  return `${n} photo${n === 1 ? "" : "s"}`;
}

/** " · 3 photos" — the artboard's Today/NCR "why" suffix, omitted when there are none. */
export function photoCountSuffix(photoCount) {
  const label = photoCountLabel(photoCount);
  return label ? ` · ${label}` : "";
}

/**
 * Group 2: NCRs recorded, not raised — warn once ageing past 3 days, mute
 * below. No id badge: the artboard (mockups/cockpit-v3/Main.dc.html) shows
 * no id on this row, so the NCR number is carried on the row for the review
 * action but never rendered as a subId. `photoCount` (GET /api/cockpit/today
 * -> CisCockpitToday, computed server-side from NcrPhotos-<CODE>) drives the
 * artboard's " · N photos" suffix.
 */
export function shapeNcrsRecorded(code, rows) {
  return (rows || []).map((row) => ({
    key: `${code}-ncr-${row.ncrNumber}`,
    code,
    title: row.title || "",
    subId: null,
    // No weekday here — unlike the other two groups' "why" text, the
    // artboard's "Recorded" line is date-only ("Recorded 27 Aug"), never
    // "Recorded Thu 27 Aug" (mockups/cockpit-v3/Main.dc.html).
    why: `Recorded ${formatDayShort(row.raisedDate)}${photoCountSuffix(row.photoCount)}`,
    who: initialsName(row.dutyDept),
    chipText: `${row.ageDays}d`,
    chipClass: row.ageDays >= 3 ? "warn" : "mute",
    action: "review",
    ncrNumber: row.ncrNumber,
  }));
}

/**
 * Group 3: results missing — inspection date passed, no outcome recorded.
 * The row is NOT yet inspected (that is the whole reason it is in this
 * group), so the caption reads "Planned <date>" / "Re-inspection planned
 * <date>" rather than an inspected/re-inspected verb, which would read as
 * if the outcome had already been recorded (owner 28 Sep — deviation from
 * the artboard's "Inspected"/"Re-inspected" text, logged in
 * docs/reviews/ARTBOARD-TEXT-DEVIATIONS.md: "artboard wording implies the
 * inspection took place; results-missing rows are not yet inspected (owner
 * 28 Sep)"). The schedule row's own `sourceInspectionNumber`
 * (SPEC-CIS-SITE-MANAGER-COCKPIT-V3-001 §4.3: set only when the booking
 * traces back to a register row) is the real, data-driven signal for the
 * re-inspection distinction — non-zero means a re-inspection.
 */
export function shapeResultsMissing(code, rows) {
  return (rows || []).map((row) => {
    const isReinspection = Number(row.sourceInspectionNumber || 0) > 0;
    const why = isReinspection
      ? `Re-inspection planned ${formatDay(row.plannedDate)}`
      : `Planned ${formatDay(row.plannedDate)}`;
    return {
      key: `${code}-missing-${row.scheduleRef}`,
      code,
      title: row.title || "",
      subId: isHumanReadableRef(row.scheduleRef) ? row.scheduleRef : null,
      why,
      who: initialsName(row.assignedTo),
      chipText: `${row.daysLate}d`,
      chipClass: "warn",
      action: "remind",
      scheduleRef: row.scheduleRef,
      // Owner retest 3: carried so the Report's "Results missing" rows and
      // "Open in Plan" can resolve the same item without a backend field.
      plannedDate: row.plannedDate || "",
      category: row.category || "",
      assignedTo: row.assignedTo || "",
    };
  });
}

/**
 * Side card: on site today. `plannedTime` (GET /api/cockpit/today ->
 * CisCockpitToday, InspectionSchedule.PlannedTime) is optional — a row with
 * no time recorded renders none, same as before, rather than inventing one.
 */
export function shapeOnSiteToday(code, rows) {
  return (rows || []).map((row, index) => ({
    key: `${code}-onsite-${row.scheduleRef || index}`,
    code,
    title: row.title || "",
    subId: isHumanReadableRef(row.scheduleRef)
      ? row.scheduleRef
      : isHumanReadableRef(row.inspectionType)
        ? row.inspectionType
        : null,
    who: initialsName(row.assignedTo),
    category: row.category || row.stage || "",
    plannedTime: row.plannedTime || null,
  }));
}

/**
 * Side card: week report readiness. `reportReadiness.lines` is the only
 * source Today carries (report status/draft version comes from the Reports
 * endpoint and is out of scope for this screen) — the worst non-green line
 * drives the chip, or an "all clear" ok chip when every line is green.
 */
export function shapeReadinessLine(code, readiness) {
  const lines = (readiness && readiness.lines) || [];
  const worst = lines.filter((line) => !line.green).sort((a, b) => b.count - a.count)[0];
  if (!worst) return { code, chipText: "All clear", chipClass: "ok" };
  const labels = {
    inspection_results: "results missing",
    inspection_photos: "without photos",
    ncr_decisions: "NCRs awaiting decision",
  };
  return { code, chipText: `${worst.count} ${labels[worst.id] || worst.id}`, chipClass: "warn" };
}

/** ISO week number for a `YYYY-MM-DD` date string ("Week 35 reports"). */
export function isoWeekNumber(dateStr) {
  const parsed = Date.parse(`${dateStr}T00:00:00Z`);
  if (!Number.isFinite(parsed)) return null;
  const target = new Date(parsed);
  target.setUTCDate(target.getUTCDate() + 4 - (target.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(target.getUTCFullYear(), 0, 1));
  return Math.ceil(((target - yearStart) / DAY_MS + 1) / 7);
}

const READINESS_LABELS = {
  inspection_results: (n) => `${n} result${n === 1 ? "" : "s"} missing`,
  inspection_photos: (n) => `${n} inspection${n === 1 ? "" : "s"} without photos`,
  ncr_decisions: (n) => `${n} NCR${n === 1 ? "" : "s"} awaiting decision`,
};

/**
 * Side card, per project — mockups/cockpit-v3/Main.dc.html "Week 35
 * reports": a readiness violation shows the worst line ("bad"); otherwise
 * the project's own report archive for that week decides the chip: no
 * report yet -> "ready to confirm" ("warn"), Draft -> "draft vN" ("ok"),
 * Approved -> "approved" ("acc"), Issued -> "published" ("ok"). Sourced from
 * GET /api/cockpit/reports (azure-functions/cis/CisCockpitReports), which
 * carries both the readiness and the archive together.
 */
export function shapeReadinessCard({ code, readiness, reports }) {
  const weekStart = readiness && readiness.weekStart;
  const lines = (readiness && readiness.lines) || [];
  const worst = lines.filter((line) => !line.green).sort((a, b) => b.count - a.count)[0];
  if (worst) {
    const label = READINESS_LABELS[worst.id] ? READINESS_LABELS[worst.id](worst.count) : `${worst.count} ${worst.id}`;
    return { code, chipText: label, chipClass: "bad" };
  }
  const currentReport = (reports || []).find((r) => r.weekStart === weekStart);
  if (!currentReport) return { code, chipText: "ready to confirm", chipClass: "warn" };
  if (currentReport.reportStatus === "Draft") return { code, chipText: `draft v${currentReport.generationCount || 1}`, chipClass: "ok" };
  if (currentReport.reportStatus === "Approved") return { code, chipText: "approved", chipClass: "acc" };
  if (currentReport.reportStatus === "Issued") return { code, chipText: "published", chipClass: "ok" };
  return { code, chipText: currentReport.reportStatus || "unknown", chipClass: "mute" };
}

/**
 * Whole "Week NN reports" side card from the `/api/cockpit/reports`
 * envelope. Per-project failures (`envelope.errors`, azure-functions/cis/
 * CisCockpitReports §4.9 partial-project failure) get their own visible
 * "Unavailable" card row — same treatment as Today's errors[] — instead of
 * silently dropping out of the card. Call `shapeReportsScreen(null, {
 * failed: true, errorCode })` for the case where the whole `/reports` call
 * failed (network error, non-2xx): that renders one "Unavailable" row for
 * the card rather than an empty or missing card.
 */
export function shapeReportsScreen(envelope, options = {}) {
  if (options.failed) {
    return {
      weekStart: null,
      cards: [{ code: null, chipText: `Unavailable — ${options.errorCode || "reports_unreachable"}`, chipClass: "bad", unavailable: true }],
      failed: true,
    };
  }
  const projects = (envelope && envelope.projects) || [];
  const errors = (envelope && envelope.errors) || [];
  const cards = projects.map((project) => shapeReadinessCard(project));
  for (const err of errors) {
    cards.push({ code: err.code, chipText: `Unavailable — ${err.error}`, chipClass: "bad", unavailable: true });
  }
  return { weekStart: envelope && envelope.weekStart, cards, failed: false };
}

/**
 * The one project-filter rule every cockpit screen applies (Today, Plan,
 * NCRs, Weekly report) so the top bar's project pill and every count derived
 * from the rows it filters never disagree — App.jsx applies this to each
 * screen's raw envelope (projects/errors/items, all of which carry a `code`
 * field) before shaping, so `lead` text, nav badges and the readiness/report
 * side cards are all computed from the same filtered set the rows come from.
 * An empty selection means "all projects" (the pill's own default: SPEC-
 * CIS-SITE-MANAGER-COCKPIT-V3-001 top bar), never "none".
 */
export function filterByProjectCodes(rows, selectedCodes) {
  if (!selectedCodes || !selectedCodes.length) return rows || [];
  return (rows || []).filter((row) => selectedCodes.includes(row.code));
}

export const GROUP_HEADINGS = {
  reinspectionsOverdue: "Re-inspections to book",
  ncrsRecorded: "NCRs waiting for your decision",
  resultsMissing: "Results missing — the inspection date has passed and no result is recorded",
};

/**
 * Converts the pure shaping output above into the view model Today.jsx
 * renders — the same shape the fixture (fixtures/main.js) is written in, so
 * one component renders both live data and the fixture unchanged.
 *
 * `reportsScreen` (shapeReportsScreen output, from GET /api/cockpit/reports)
 * drives the "Week NN reports" card when available — the real archive state
 * (ready to confirm / draft vN / approved / published) the artboard shows.
 * When the reports read is unavailable (still loading, or its own fetch
 * failed), this falls back to the readiness-only line already carried on
 * the Today envelope, so the card degrades to "Week report readiness"
 * rather than disappearing.
 */
export function toTodayViewModel(model, reportsScreen, { readinessLoading = false } = {}) {
  const total =
    model.groups.reinspectionsOverdue.length + model.groups.ncrsRecorded.length + model.groups.resultsMissing.length;
  const readinessCardTitle =
    reportsScreen && reportsScreen.weekStart
      ? `Week ${isoWeekNumber(reportsScreen.weekStart)} reports`
      : "Week report readiness";
  const readinessSource = reportsScreen ? reportsScreen.cards : model.readinessLines;
  return {
    lead: `${total} item${total === 1 ? "" : "s"} wait on your decision across ${model.projectCodes.length} project${
      model.projectCodes.length === 1 ? "" : "s"
    } · nothing ages out silently`,
    groups: model.groups,
    onSiteToday: model.onSiteToday.map((row) => ({
      key: row.key,
      time: row.plannedTime || null,
      timeClass: "t3",
      title: row.title,
      sub: [row.code, row.subId, row.who, row.category].filter(Boolean).join(" · "),
    })),
    readinessCardTitle,
    readinessLoading: Boolean(readinessLoading),
    readinessWeekStart: (reportsScreen && reportsScreen.weekStart) || null,
    readinessLines: readinessSource.map((line, index) => ({
      key: `readiness-${line.code || index}`,
      code: line.code || null,
      label: line.code || "Reports",
      chipText: line.chipText,
      chipClass: line.chipClass,
    })),
  };
}

/** Whole Today screen model from the `/api/cockpit/today` envelope. */
export function shapeTodayScreen(envelope) {
  const projects = (envelope && envelope.projects) || [];
  const errors = (envelope && envelope.errors) || [];
  const reinspectionsOverdue = [];
  const ncrsRecorded = [];
  const resultsMissing = [];
  const onSiteToday = [];
  const readinessLines = [];
  for (const project of projects) {
    reinspectionsOverdue.push(...shapeReinspectionsOverdue(project.code, project.groups.reinspectionsOverdue));
    ncrsRecorded.push(...shapeNcrsRecorded(project.code, project.groups.ncrsRecorded));
    resultsMissing.push(...shapeResultsMissing(project.code, project.groups.resultsMissing));
    onSiteToday.push(...shapeOnSiteToday(project.code, project.onSiteToday));
    readinessLines.push(shapeReadinessLine(project.code, project.reportReadiness));
  }
  const byAge = (a, b) => (Number(b.chipText) || 0) - (Number(a.chipText) || 0);
  return {
    generatedAtUtc: envelope && envelope.generatedAtUtc,
    projectCodes: projects.map((p) => p.code),
    errors,
    counts: (envelope && envelope.counts) || {
      today: reinspectionsOverdue.length + resultsMissing.length,
      ncrs: ncrsRecorded.length,
      reports: readinessLines.filter((r) => r.chipClass !== "ok").length,
    },
    groups: {
      reinspectionsOverdue: reinspectionsOverdue.sort(byAge),
      ncrsRecorded: ncrsRecorded.sort(byAge),
      resultsMissing: resultsMissing.sort(byAge),
    },
    onSiteToday,
    readinessLines,
  };
}

/**
 * Plan screen — shapes the `/api/cockpit/plan` envelope (azure-functions/cis/
 * shared/cockpit.js `planView`) into the week grid Plan.jsx renders
 * (mockups/cockpit-v3/Plan.dc.html §4.3). Pure functions, same
 * live/fixture-through-one-pipeline discipline as the Today shapers above.
 *
 * `planView` carries only `{ upn, teams }` per lane — no display name, no
 * team/role label like "Hull and coating" (that string in the artboard is
 * an illustrative sample, not a backend field). This module derives a
 * display name the same way Today does (`initialsName`) and a team label
 * from the real `teams[]` the lane is rostered on.
 */

const WEEKDAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** "31 Aug" from an ISO date string. */
export function formatDayShort(value) {
  const parsed = Date.parse(`${value}T00:00:00Z`);
  if (!Number.isFinite(parsed)) return String(value || "");
  const d = new Date(parsed);
  return `${d.getUTCDate()} ${MONTH[d.getUTCMonth()]}`;
}

/** The seven ISO dates (Mon..Sun) starting at `weekStart` (Day redesign phase 1, D9: the yard works Saturdays and some Sundays). */
export function weekdayDates(weekStart) {
  const start = Date.parse(`${weekStart}T00:00:00Z`);
  if (!Number.isFinite(start)) return [];
  const out = [];
  for (let i = 0; i < 7; i += 1) {
    const d = new Date(start + i * DAY_MS);
    out.push(d.toISOString().slice(0, 10));
  }
  return out;
}

/**
 * Canonical ephemeral/test project codes (azure-functions/cis/shared/
 * ephemeral-projects.js is the server-side source of truth; mirrored here
 * as a literal list because the client bundle has no build-time import path
 * into the function app's shared/ tree). A project is added to this list
 * only as a fixed literal, never derived from live data — the same
 * discipline the server module documents.
 */
const TEST_PROJECT_CODES = ["EPHA", "EPHB", "SS411R", "SS411S"];

/**
 * True for a real person UPN (`name@tenant-domain`) — false for a service
 * principal roster entry (`app:<guid>`, the CI identity on SS411R's
 * roster), any other non-email string, or an empty/whitespace value. The
 * planView lane placeholder (`{ upn: "", teams: [] }`) is deliberately not a
 * "person" either; callers check for that literal `""` separately before
 * calling this.
 */
export function isPersonUpn(upn) {
  const value = String(upn || "").trim();
  if (!value) return false;
  return /^[^\s@:]+@[^\s@]+\.[^\s@]+$/.test(value);
}

/**
 * Plan lane filter (SPEC-CIS-SITE-MANAGER-COCKPIT-V3-001 §4.3 lanes; owner
 * live-use defect 2026-09-30: with "All projects" selected the cockpit
 * showed a lane for the CI service identity ("sm:app:0daaefbf-…", the
 * SS411R roster's non-person entry), one for test.user@neg-marine.com (the
 * EPHA test account) and a blank lane — none of them a real inspector to
 * book work against.
 *
 * - Never a lane for a non-person roster entry (`isPersonUpn` false) — the
 *   Unassigned placeholder (`upn === ""`) is the one deliberate exception,
 *   left to shapePlanScreen's own population check below.
 * - A specific project-filter selection scopes lanes to that project's own
 *   roster, same as every other filtered field (items/unbooked/imports).
 * - "All projects" (empty selection) scopes lanes to real projects: a lane
 *   rostered ONLY on ephemeral/test codes (`TEST_PROJECT_CODES`) — such as
 *   the EPHA test account — is hidden until a test project is explicitly
 *   selected. A lane rostered on a real project AND a test project (cross-
 *   project lending) still shows, unchanged.
 *
 * Also narrows each surviving lane's `teams[]` to the in-scope subset
 * (owner live-use defect 2026-10-06: on SS418, the site manager's own lane
 * showed "SS412413 team" although he was viewing SS418 — `planView`'s
 * `rosterByProject` carries EVERY project a person is rostered on, in
 * roster-iteration order, and `laneTeamLabel` always takes `teams[0]`; with
 * "SS412413" simply ordered first in that unfiltered list, the label named
 * the wrong project regardless of which one was selected). Narrowing here
 * — before `laneTeamLabel` ever sees the array — means the label always
 * names a project within the current scope: the one project selected, one
 * of several selected projects, or (All projects) one of the person's real
 * (non-test) projects.
 */
export function filterPlanLanes(lanes, selectedCodes) {
  const codes = selectedCodes && selectedCodes.length ? selectedCodes : null;
  const inScope = (teams) => (codes ? teams.filter((team) => codes.includes(team)) : teams.filter((team) => !TEST_PROJECT_CODES.includes(team)));
  return (lanes || [])
    .filter((lane) => {
      if (lane.upn === "") return true; // the deliberate Unassigned placeholder
      if (!isPersonUpn(lane.upn)) return false;
      return inScope(lane.teams || []).length > 0;
    })
    .map((lane) => {
      if (lane.upn === "") return lane;
      const scoped = inScope(lane.teams || []);
      // `rosterTeams` keeps the person's FULL roster membership (the backend
      // refuses an assignment to anyone not on the item's own project
      // roster), while `teams` is narrowed above purely for the lane label.
      const rosterTeams = lane.rosterTeams || lane.teams || [];
      return scoped.length ? { ...lane, teams: scoped, rosterTeams } : { ...lane, rosterTeams };
    });
}

/**
 * Whether a Plan lane may receive an item of project `code`. The backend
 * rule (inspector_not_on_roster): an item can be assigned only to an
 * inspector on its own project's roster. The Unassigned lane (upn "")
 * accepts everything; an item with no project code is not constrained.
 */
export function laneAcceptsProject(lane, code) {
  if (!lane || lane.upn === "" || !code) return true;
  return (lane.rosterCodes || lane.teams || []).includes(code);
}

/**
 * Lane team label: always a single team, first-listed (SPEC-CIS-SITE-
 * MANAGER-COCKPIT-V3-001 AC-02: "an inspector rostered on both A and B
 * while that inspector's lane is labelled with team B" — the roster grants
 * the inspector cross-project access, but the lane label is never a
 * concatenation of every project they're rostered on). "Unassigned" when
 * the lane carries no roster membership at all.
 */
export function laneTeamLabel(teams) {
  const list = teams || [];
  if (!list.length) return "Unassigned";
  return `${list[0]} team`;
}

/**
 * Tile chip from the schedule row's real status fields — SCHEDULE_STATUSES
 * (validation.js): Planned/Completed/Skipped/Cancelled, plus the additive
 * `done`/`unplanned` fields `mergeScheduleAndRegister` (azure-functions/cis/
 * shared/cockpit.js) attaches to every Plan grid row (PR #1958). A row can
 * carry `done: true` while its own `scheduleStatus` is still "Planned" (the
 * ScheduleStatus PATCH never landed even though the capture happened) — the
 * redesign's legend (owner-approved mockup, DEC-CHAT-20261007-412) promises
 * "result recorded" shows as `done` regardless of that stale status field, so
 * `row.done` is checked ahead of the raw status. No invented states.
 */
export function planTileChip(row, today) {
  if (row.scheduleStatus === "Skipped") return { chipText: "hold", chipClass: "mute" };
  if (row.scheduleStatus === "Completed" || row.done) return { chipText: "done", chipClass: "ok" };
  // Owner retest 4: "booked" means ready, nothing left to do — a date AND an
  // inspector. A dated row nobody is assigned to still needs a person.
  if (!String(row.assignedTo || "").trim()) return { chipText: "needs inspector", chipClass: "warn" };
  if (row.plannedDate === today) return { chipText: "today", chipClass: "warn" };
  return { chipText: "booked", chipClass: "acc" };
}

/**
 * Unique identity of a plan row/tile within a project. A recorded inspection
 * with no schedule row ("unplanned" done tile) carries scheduleRef "" — many
 * such tiles would share one key and every click would resolve to the first.
 * Those are keyed by their recorded inspection number instead.
 */
export function planRowKey(row) {
  if (row && row.scheduleRef) return row.scheduleRef;
  const n = row && (row.recordedInspectionNumber || row.inspectionNumber);
  return n ? `rec#${n}` : "";
}
/** Project-qualified selection key of a grid tile (`code:identity`). */
export function planTileKey(tile) {
  return `${tile.code}:${planRowKey(tile.row || tile) || tile.scheduleRef}`;
}

/**
 * One grid tile from a plan `items[]` row. `plannedTime` (optional HH:MM,
 * InspectionSchedule.PlannedTime) renders when the row carries one; a row
 * with none omits it, same as before.
 */
export function shapePlanTile(row, today) {
  const chip = planTileChip(row, today);
  return {
    key: planRowKey(row),
    scheduleRef: row.scheduleRef,
    code: row.code,
    title: row.title || "",
    assignedTo: String(row.assignedTo || "").toLowerCase(),
    plannedDate: row.plannedDate,
    plannedTime: row.plannedTime || null,
    chipText: chip.chipText,
    chipClass: chip.chipClass,
    // Owner retest 4: import provenance (SourceRunId, additive); "" for a row
    // no import created.
    sourceRunId: row.sourceRunId || "",
    importedAtUtc: row.importedAtUtc || "",
    // Full row carried through so a drag-drop upsert can resend every
    // required field (validateScheduleItem requires the whole shape, not a
    // partial patch) while changing only assignedTo/plannedDate.
    row,
  };
}

/**
 * One "due within 14 days, no date" row from an `unbooked[]` entry. No due-
 * date estimate or import-source label exists on an unbooked row (an empty
 * PlannedDate is what makes it unbooked) — both are artboard sample
 * decoration, omitted here rather than invented.
 */
export function shapeUnbookedRow(row) {
  return {
    key: row.scheduleRef,
    scheduleRef: row.scheduleRef,
    // Only a human-readable reference is ever shown (owner live-use defect:
    // a raw internal id after the title) — `scheduleRef` itself is kept
    // above for drag/drop and the upsert payload regardless.
    displayRef: isHumanReadableRef(row.scheduleRef) ? row.scheduleRef : null,
    code: row.code,
    title: row.title || "",
    category: row.category || "",
    row,
  };
}

/**
 * The single most-recent import line across all sm projects (imports[] is
 * already one entry per project, reduced to its latest committed run by
 * cockpit.js `latestImport`). `waitingCount` (Plan redesign, DEC-CHAT-
 * 20261007-412 — the "Yard schedule" panel's summary line names how many
 * new rows from that run are still sitting in the To-book backlog)
 * appends an additive clause; omitted/0 leaves the message exactly as
 * before — the "rows read"/"skipped (no date)" phrases tests anchor on
 * are unchanged either way.
 */
export function shapeLastImport(imports, waitingCount) {
  const rows = (imports || [])
    .slice()
    .sort((a, b) => String(b.importedAtUtc || "").localeCompare(String(a.importedAtUtc || "")));
  if (!rows.length) return null;
  const row = rows[0];
  const counts = row.itemCounts || {};
  const read = (counts.created || 0) + (counts.updated || 0) + (counts.duplicate || 0) + (counts.conflict || 0) + (counts.invalid || 0);
  const skipped = counts.skipped || 0;
  const waiting = Number(waitingCount || 0);
  return {
    code: row.code,
    runId: row.runId || "",
    importedAtUtc: row.importedAtUtc,
    read,
    skipped,
    // "yard schedule" (not the project code) — the import pipeline has
    // exactly one source kind (azure-functions/cis/shared/yard-schedule-
    // parse.js `parseYardSchedule`); the backend's own `imports[]` entry
    // carries no separate per-project label, so the line summarises the
    // single most-recent run across all sm projects without naming one.
    message:
      `yard schedule imported ${formatDay(row.importedAtUtc)} · ${read} row${read === 1 ? "" : "s"} read · ${skipped} skipped (no date)` +
      (waiting ? ` · ${waiting} new row${waiting === 1 ? "" : "s"} wait in To book` : ""),
  };
}

/**
 * Plan redesign (owner-approved, DEC-CHAT-20261007-412): the left "To book"
 * column replaces "Due within 14 days, no date" with `planView`'s own
 * `backlog[]` (azure-functions/cis/shared/cockpit.js — ranked re-inspections
 * overdue first, then undated rows; PR #1958). Both kinds are shaped into one
 * row here so Plan.jsx renders either through the same list.
 */

/** Chip for one backlog row — urgency-coded age for a re-inspection, a plain "no date" for an unbooked row. No invented urgency tiers beyond the existing bad/warn split Today already uses for the same ageDays field. */
export function backlogRowChip(row) {
  if (row.kind === "reinspection") {
    const days = Number(row.ageDays || 0);
    return { chipText: `${days}d`, chipClass: days >= 3 ? "bad" : "warn" };
  }
  return { chipText: "no date", chipClass: "mute" };
}

/**
 * One "To book" row. `category` (the filter pills' per-trade grouping) is
 * only ever present on the `unbooked` kind — `planView`'s reinspection
 * backlog entries carry no category/stage/ITP-ref field at all (§4.3); never
 * invented here.
 *
 * `fullUnbookedRowsByRef` (optional, scheduleRef -> the FULL schedule row
 * from `envelope.unbooked`) is how an unbooked backlog row's drag payload
 * stays correct: `planView`'s own `backlog[]` entry for an unbooked row
 * carries only `{ code, scheduleRef, title, category, stage, assignedTo }`
 * — missing `inspectionType`/`plannedTime`/`witnessParties`/`notes`, which
 * `buildDragUpsertRow` resends verbatim on every drop (SITE_MANAGER_EDITABLE
 * is the only part of the row allowed to change; every other field must be
 * the row's own real value, never `undefined`). Falls back to the trimmed
 * backlog row only when no match is found (should not happen — `unbooked`
 * is `planView`'s full superset of the backlog's unbooked subset).
 */
export function shapeBacklogRow(row, fullUnbookedRowsByRef) {
  const chip = backlogRowChip(row);
  const isReinspection = row.kind === "reinspection";
  const fullRow = !isReinspection && fullUnbookedRowsByRef ? fullUnbookedRowsByRef.get(row.scheduleRef) : null;
  return {
    // Project-qualified (codex review finding — a bare scheduleRef can
    // collide across two different projects' own schedule lists) and
    // prefixed by kind, so a caller (Plan.jsx's selection re-resolution)
    // can parse it back apart without a second lookup.
    key: isReinspection ? `reinsp:${row.code}:${row.inspectionNumber}` : `unbooked:${row.code}:${row.scheduleRef}`,
    kind: row.kind,
    code: row.code,
    scheduleRef: isReinspection ? null : row.scheduleRef || null,
    inspectionNumber: isReinspection ? row.inspectionNumber : null,
    sourceInspectionNumber: isReinspection ? row.inspectionNumber : null,
    title: row.title || "",
    displayRef: !isReinspection && isHumanReadableRef(row.scheduleRef) ? row.scheduleRef : null,
    category: row.category || "",
    stage: row.stage || "",
    assignedTo: String(row.assignedTo || "").toLowerCase(),
    chipText: chip.chipText,
    chipClass: chip.chipClass,
    sub: isReinspection ? `Our inspection ${formatDayShort(row.inspectionDate)}` : row.category || "",
    ageDays: typeof row.ageDays === "number" ? row.ageDays : null,
    inspectionDate: row.inspectionDate || null,
    // Owner retest 4: import provenance of an undated row (full schedule row).
    sourceRunId: (fullRow && fullRow.sourceRunId) || "",
    importedAtUtc: (fullRow && fullRow.importedAtUtc) || "",
    // The draggable payload a drop actually sends — see the doc comment
    // above for why this is NOT always the bare backlog entry.
    row: isReinspection ? row : fullRow || row,
  };
}

/**
 * Filter pills for the "To book" column (owner-approved mockup: "All /
 * Re-inspections N / No date N / per trade present in data") — a trade pill
 * renders only for a trade that actually has an unbooked row this page of
 * the backlog carries; it is never a fixed static list.
 */
export function backlogFilterPills(shapedRows) {
  const rows = shapedRows || [];
  const reinspCount = rows.filter((r) => r.kind === "reinspection").length;
  const noDateCount = rows.filter((r) => r.kind === "unbooked").length;
  const trades = new Map();
  for (const row of rows) {
    if (row.kind !== "unbooked" || !row.category) continue;
    trades.set(row.category, (trades.get(row.category) || 0) + 1);
  }
  const pills = [{ id: "all", label: "All", count: rows.length }];
  if (reinspCount) pills.push({ id: "reinspection", label: `Re-inspections ${reinspCount}`, count: reinspCount });
  if (noDateCount) pills.push({ id: "nodate", label: `No date ${noDateCount}`, count: noDateCount });
  for (const [trade, count] of trades) pills.push({ id: `trade:${trade}`, label: trade, count });
  return pills;
}

/** Rows visible under one filter pill id (backlogFilterPills' own ids) — "all" (or no filter) shows everything. */
export function filterBacklogRows(shapedRows, filterId) {
  const rows = shapedRows || [];
  if (!filterId || filterId === "all") return rows;
  if (filterId === "reinspection") return rows.filter((r) => r.kind === "reinspection");
  if (filterId === "nodate") return rows.filter((r) => r.kind === "unbooked");
  if (filterId.startsWith("trade:")) return rows.filter((r) => r.category === filterId.slice("trade:".length));
  return rows;
}

/** Parses a `shapeBacklogRow` `key` (`"unbooked:<code>:<scheduleRef>"` / `"reinsp:<code>:<inspectionNumber>"`) back into its parts. */
export function parseBacklogKey(key) {
  const str = String(key || "");
  const first = str.indexOf(":");
  const second = str.indexOf(":", first + 1);
  if (first < 0 || second < 0) return null;
  return { kind: str.slice(0, first), code: str.slice(first + 1, second), ref: str.slice(second + 1) };
}

/** Every grid tile across every lane/day cell in a `shapePlanScreen` model, flattened. */
export function allPlanTiles(model) {
  const out = [];
  for (const lane of (model && model.lanes) || []) {
    for (const cell of lane.cells || []) {
      for (const tile of cell) out.push(tile);
    }
  }
  return out;
}

/** The grid tile matching a `${code}:${scheduleRef}` compound key, or null. */
export function findPlanTileByCompoundKey(model, compoundKey) {
  return allPlanTiles(model).find((t) => planTileKey(t) === compoundKey) || null;
}

/**
 * The grid tile a disappeared backlog row most likely became — e.g. the
 * site manager just booked it, so it no longer sits in `model.backlog`
 * (planView's own ranked list, PR #1958) but should now be ON the grid.
 * Matched by the SAME real-world identity the booking drop itself carries
 * forward (`buildDragUpsertRow`/`buildBookingRow`), never by the old
 * backlog key, which no grid tile ever carries:
 *  - an `unbooked` row -> the tile sharing its project + scheduleRef (the
 *    row simply got a plannedDate).
 *  - a `reinspection` row -> the tile whose own row carries that same
 *    `sourceInspectionNumber` (a brand-new scheduleRef was minted for it).
 */
export function findPlanTileForBacklogIdentity(model, parsedKey) {
  if (!parsedKey) return null;
  const tiles = allPlanTiles(model);
  if (parsedKey.kind === "unbooked") {
    return tiles.find((t) => t.code === parsedKey.code && t.scheduleRef === parsedKey.ref) || null;
  }
  if (parsedKey.kind === "reinsp") {
    const num = Number(parsedKey.ref);
    return tiles.find((t) => t.code === parsedKey.code && Number((t.row && t.row.sourceInspectionNumber) || 0) === num) || null;
  }
  return null;
}

/**
 * Re-resolves a Plan screen selection from the CURRENT model on every
 * render (codex review finding: the old code snapshotted the selected
 * row/tile's data once, at click time, which went stale the moment the
 * model refreshed — e.g. after that same row got booked and moved to a
 * different cell, or vanished from the backlog page it was on). `selectedRef`
 * is a pure `{ kind, key }` pointer, never a data snapshot; this is the
 * ONLY place that data is looked up.
 */
export function resolvePlanSelection(selectedRef, model) {
  if (!selectedRef || !model) return null;
  if (selectedRef.kind === "backlog") {
    const row = (model.backlog || []).find((r) => r.key === selectedRef.key);
    if (row) return { kind: "backlog", key: row.key, data: row };
    const tile = findPlanTileForBacklogIdentity(model, parseBacklogKey(selectedRef.key));
    return tile ? { kind: "tile", key: planTileKey(tile), data: tile } : null;
  }
  const tile = findPlanTileByCompoundKey(model, selectedRef.key);
  return tile ? { kind: "tile", key: selectedRef.key, data: tile } : null;
}

/**
 * "Open in Plan" target -> the selection pointer to apply, or null while the
 * target's tile is not in the loaded week model (the caller keeps waiting).
 */
export function planFocusSelection(focusTarget, model) {
  if (!focusTarget) return null;
  const key = `${focusTarget.code}:${focusTarget.scheduleRef}`;
  return resolvePlanSelection({ kind: "tile", key }, model) ? { kind: "tile", key } : null;
}

/**
 * The right-hand detail panel (owner-approved redesign, DEC-CHAT-20261007-
 * 412) always shows real content — there is no idle "nothing selected"
 * state in the approved canvas. Before the site manager clicks anything,
 * this picks the same thing clicking the first visible row would select:
 * the first "To book" backlog row, or — when the backlog is empty — the
 * first grid tile. Returns null only when the week has neither (an empty
 * week), which the caller still has to handle.
 */
export function defaultPlanSelection(model) {
  const firstBacklog = (model && model.backlog && model.backlog[0]) || null;
  if (firstBacklog) return { kind: "backlog", key: firstBacklog.key, data: firstBacklog };
  const firstTile = allPlanTiles(model)[0] || null;
  return firstTile ? { kind: "tile", key: planTileKey(firstTile), data: firstTile } : null;
}

/**
 * dnd-kit requires every draggable id inside one `DndContext` to be
 * globally unique. A bare `scheduleRef`/`inspectionNumber` can collide
 * across two DIFFERENT projects' own lists (codex review finding) — every
 * drag id Plan.jsx hands to `useDraggable` is built here, always project-
 * qualified, so that can never happen.
 */
export function planTileDragId(tile) {
  return `tile:${planTileKey(tile)}`;
}
export function backlogRowDragId(row) {
  return row.kind === "reinspection" ? `backlog-reinsp:${row.code}:${row.inspectionNumber}` : `backlog-unbooked:${row.code}:${row.scheduleRef}`;
}
export function pinnedRowDragId(pinned) {
  return `pinned:${pinned.code}:${pinned.scheduleRef || pinned.inspectionNumber}`;
}

/**
 * Scopes the accumulated raw backlog rows (server page 1 + any further
 * pages the site manager fetched) to the selected project(s), and derives
 * `backlogTotal`/`canShowMore` correctly for that scope — `CisCockpitPlan`
 * has no per-project backlog count and no project-filter query parameter
 * (codex review finding, App.jsx:724): its own `backlogTotal` is always
 * the cross-project total, which previously leaked straight through as a
 * scoped "N of M" (wrong once a filter is active) and made "Show more"
 * look like it could run forever (the inference `backlog.length <
 * backlogTotal` never becomes false while comparing a SCOPED numerator
 * against an UNSCOPED denominator). Unscoped ("All projects"), the
 * server's own total is exactly right and is used verbatim.
 */
export function computeBacklogPaging({ rawBacklog, rawTotal, selectedCodes }) {
  const raw = rawBacklog || [];
  const total = typeof rawTotal === "number" ? rawTotal : raw.length;
  const exhausted = raw.length >= total;
  const scoped = Boolean(selectedCodes && selectedCodes.length);
  const backlog = scoped ? filterByProjectCodes(raw, selectedCodes) : raw;
  return {
    backlog,
    // Scoped: exactly what has been scanned and matched so far — never the
    // server's cross-project number, which would overstate a scoped total.
    // Unscoped: the server's own total, unchanged.
    backlogTotal: scoped ? backlog.length : total,
    // "Show more" stays available only while the server itself still has
    // unread raw rows — once truly exhausted, every matching row (scoped
    // or not) has already been found; never offered past that point.
    canShowMore: !exhausted,
    backlogExhausted: exhausted,
  };
}

/**
 * The "Show more" paging loop itself, as a pure function App.jsx can call
 * with a real `fetchPage` — codex review round 2, App.jsx:825 (P1): the
 * previous inline loop read `backlogExtra.length` from App.jsx's own
 * render-time CLOSURE to compute each page's offset; `setBacklogExtra`
 * never mutates that closure variable, so every iteration recomputed the
 * SAME offset and re-fetched the SAME page forever. This tracks the
 * running total in a local variable instead, so the offset always reflects
 * every page actually fetched so far THIS call — and caps at `maxPages` as
 * a hard backstop regardless of why a loop might otherwise not terminate.
 *
 * `fetchPage(offset)` resolves to that page's raw row array (`[]` when the
 * server has nothing left at that offset). Stops when a page is empty
 * (`exhausted: true`), when a page contains a row `isMatch` accepts, or
 * once `maxPages` pages have been fetched (`cappedOut: true`).
 */
export async function fetchBacklogPagesUntilMatch({ fetchPage, pageOneLength, initialExtra = [], isMatch = () => true, maxPages = 25 }) {
  let accumulated = [...initialExtra];
  for (let i = 0; i < maxPages; i += 1) {
    const offset = pageOneLength + accumulated.length;
    const page = await fetchPage(offset);
    if (!page || !page.length) return { extra: accumulated, exhausted: true, cappedOut: false };
    accumulated = [...accumulated, ...page];
    if (page.some(isMatch)) return { extra: accumulated, exhausted: false, cappedOut: false };
  }
  return { extra: accumulated, exhausted: false, cappedOut: true };
}

/**
 * Looks up `find(cachedRows)` in a project's cache entry; a cache HIT that
 * does not contain a match refetches fresh (via `fetchFresh`) ONCE before
 * concluding there is none (codex review round 3, App.jsx:220): a cache
 * entry built before the thing being looked for was ever recorded — by
 * this site manager's own session elsewhere, by another site manager, or
 * by the capture app — must never be trusted as "it doesn't exist" purely
 * because the project's cache entry is present. A true cache MISS (no
 * entry at all) already fetches fresh regardless; this only changes what
 * happens on a HIT that doesn't contain a match. `fetchFresh` resolves to
 * `{ ok, rows }`; on success the cache is updated with the fresh rows.
 */
export async function lookupWithCacheRefresh({ cache, code, find, fetchFresh }) {
  const cached = cache.get(code);
  if (cached) {
    const hit = find(cached);
    if (hit) return { ok: true, value: hit };
  }
  const fresh = await fetchFresh();
  if (!fresh.ok) return { ok: false, value: null };
  cache.set(code, fresh.rows);
  return { ok: true, value: find(fresh.rows) };
}

/**
 * The InspectionRegister row (from `GET /api/projects/{code}/inspections`,
 * CisInspectionsQuery) matching a given inspection number — resolves a Plan
 * tile/backlog row's `clientCaptureId`, which neither the Plan envelope nor
 * `planView` carries for any row (closes the "detail panel needs photos"
 * gap without a backend change: codex review).
 */
export function findRegisterRowByInspectionNumber(rows, inspectionNumber) {
  if (!inspectionNumber) return null;
  return (rows || []).find((r) => Number(r.inspectionNumber) === Number(inspectionNumber)) || null;
}

/**
 * The NCR (from `GET /api/projects/{code}/ncrs`, CisNcrsQuery) whose
 * `sourceInspectionNumber` matches — an open one (not Closed/Cancelled)
 * takes priority over a closed one; the most recently numbered match when
 * several are open. Closes the "detail panel needs Open NCR" gap the same
 * way as `findRegisterRowByInspectionNumber` above — no backend change.
 */
export function findNcrForInspection(ncrRows, inspectionNumber) {
  if (!inspectionNumber) return null;
  const matches = (ncrRows || []).filter((r) => Number(r.sourceInspectionNumber || 0) === Number(inspectionNumber));
  if (!matches.length) return null;
  const open = matches.filter((r) => r.status !== "Closed" && r.status !== "Cancelled").sort((a, b) => b.ncrNumber - a.ncrNumber)[0];
  return open || matches.slice().sort((a, b) => b.ncrNumber - a.ncrNumber)[0];
}

/**
 * A schedule row's own `scheduleRef` for a recorded inspection number, from
 * Plan's own already-loaded rows (`recordedInspectionNumber`/
 * `linkedInspectionNumber` — `mergeScheduleAndRegister`, azure-functions/cis/
 * shared/cockpit.js). Report's `inspection_photos` readiness line (§4.6)
 * carries only `inspectionNumber`, never a `scheduleRef` — `POST /api/
 * cockpit/remind` needs one — so this is how "Remind inspector" there can
 * ever be enabled (codex review gap (b)). Returns `null` when Plan's own
 * data was never loaded this session, or carries no matching row — the
 * caller disables the action rather than guessing a scheduleRef.
 */
export function findScheduleRefForInspectionNumber(scheduleRows, inspectionNumber) {
  if (!inspectionNumber || !scheduleRows) return null;
  const row = scheduleRows.find(
    (r) => Number(r.recordedInspectionNumber || 0) === Number(inspectionNumber) || Number(r.linkedInspectionNumber || 0) === Number(inspectionNumber)
  );
  return row ? row.scheduleRef || null : null;
}

/**
 * Cockpit-driven yard-schedule import (SPEC-CIS-SITE-MANAGER-COCKPIT-V3-001
 * §4.3, DEC-CHAT-20260930-204): the Plan screen's "Import yard schedule"
 * button, reusing the existing preview/commit routes verbatim. Import is a
 * single-project action — the route it calls (`POST /api/projects/{code}/
 * schedule/imports`) takes exactly one `{code}`, and a wrong-project import
 * cannot be undone by the parser. "All projects" (TopBar's `allSelected`,
 * empty selection or every code checked) has no single `{code}` to send, so
 * the screen refuses client-side before any upload rather than guessing
 * which project the file is for.
 */
export const IMPORT_REFUSED_MESSAGE =
  'Pick one project in the top bar before importing a yard schedule — import is refused while "All projects" is selected.';

/**
 * Owner retest 4: the wording for a refused preview. An `empty_result`
 * refusal that carries `otherShips` (CisScheduleImports, additive) means the
 * whole file is for another ship; say so plainly. Every other refusal keeps
 * the previous "Import refused: <reason>" wording.
 */
export function describeImportRefusal(result, projectCode) {
  const body = (result && result.body) || {};
  const ships = Array.isArray(body.otherShips) ? body.otherShips.filter((s) => s && s.ship) : [];
  if (body.errorCode === "empty_result" && ships.length) {
    const fileFor = ships.map((s) => `${s.ship} (${s.rows} row${s.rows === 1 ? "" : "s"})`).join(" and ");
    const select = ships.length === 1 ? ships[0].ship : "the matching project";
    return `This file is for ${fileFor}; you are importing into ${projectCode}. Select ${select} to import it.`;
  }
  const code = body.errorCode || body.message || `http_${(result && result.status) || 0}`;
  const plain = IMPORT_REFUSAL_TEXT[code] || (result && result.status >= 500 ? IMPORT_REFUSAL_TEXT.internal_error : null);
  return `Import refused: ${plain || code}`;
}

/**
 * Plain-English wording for every refusal the import routes can answer
 * (CisScheduleImports / yard-schedule-parse). Each says what is wrong and
 * what to do next. An unknown code still shows the code, never nothing.
 */
const IMPORT_REFUSAL_TEXT = {
  bad_container: "this is not an Excel schedule (.xlsx or .xls). Open the yard's schedule in Excel, save it as a workbook and upload that.",
  multipart_invalid: "the file was empty or did not upload. Choose the schedule file again; if it keeps failing, re-save it from Excel.",
  unreadable_file: "the file could not be opened - it looks damaged or incomplete. Open it in Excel, save a fresh copy and upload that, or ask the yard to send it again.",
  password_protected: "the file is password-protected. Open it in Excel, remove the password (File > Info > Protect Workbook), save and upload again.",
  file_too_large: "the file is larger than 5 MB. Remove unused sheets or old weeks and upload again, or split it into two files.",
  caps_exceeded: "the file is too big to import in one go (too many rows, columns or sheets). Split it by week or by sheet and upload each part.",
  macro_bearing: "the workbook contains macros. Save it as a plain .xlsx without macros and upload that.",
  format_unrecognized: "this file does not look like a yard schedule - the column headings were not found. Upload the yard's daily booking list, block plan or ITP list unchanged, with its original headings.",
  empty_result: "the file was read but holds no inspection rows for this project. Check that it is the right schedule and that this project is selected.",
  format_invalid: "that schedule type is not supported. Leave the type on automatic and try again.",
  format_unsupported: "that schedule type is not supported. Leave the type on automatic and try again.",
  witness_codes_invalid: "the witness-code setting is not valid. Clear it and try again.",
  forbidden: "you are not on the site-manager list for this project. Ask for access, or select the project you manage.",
  unauthenticated: "you are signed out. Sign in again and retry the import.",
  project_not_admitted: "this project is not switched on for the cockpit yet.",
  internal_error: "the server could not process this file. Nothing was imported. Try once more; if it fails again, send the file to support.",
  transient_upstream: "SharePoint is busy right now. Nothing was imported. Wait a minute and try again.",
  network_error: "the upload did not reach the server. Check your connection and try again.",
  timeout: "the server took too long to answer. Nothing was imported. Try again.",
  http_0: "the upload did not reach the server. Check your connection and try again.",
};

/** The one project code an import targets, or null when none/more than one is selected ("All projects"). */
export function singleSelectedProjectCode(selectedCodes) {
  const codes = selectedCodes || [];
  return codes.length === 1 ? codes[0] : null;
}

/**
 * Turns a preview or commit response's own counts object into renderable
 * `{ label, count }` rows, using exactly the keys present — never inventing
 * one absent from that response. The preview and commit routes answer two
 * different shapes depending on the parsed format (`shared/schedule-
 * import.js` `computeDiff`/`commitImport`): ledger preview nests `{ count,
 * sample }` per bucket (creates/updates/retires/unchanged/skipped); painting
 * preview nests the same shape under different bucket names (creates/moves/
 * duplicates/vanished/ignored_completed/skipped); commit answers a flat
 * counts object (created/updated/duplicate/cancelled/vanishedCancelled/
 * ignored_completed/invalid/error/remaining). This one function reads
 * either shape without special-casing which kind produced it.
 */
export function shapeImportCounts(source) {
  const out = [];
  if (!source || typeof source !== "object") return out;
  for (const [key, value] of Object.entries(source)) {
    if (value && typeof value === "object" && typeof value.count === "number") {
      out.push({ label: key, count: value.count });
    } else if (typeof value === "number") {
      out.push({ label: key, count: value });
    }
  }
  return out;
}

/**
 * Owner row-level selection (6 Oct 2026 feedback: a 786-row preview with a
 * single Apply committed the whole project). `preview.candidates` is the
 * additive bounded row list `shared/schedule-import.js` `buildCandidates`
 * attaches to BOTH the ledger and painting preview shapes verbatim — this
 * reads it the same way `shapeImportCounts` reads counts: the server's own
 * keys, never invented, [] when the response does not carry it (older
 * cached run records, or a non-object preview).
 */
export function shapeImportCandidates(preview) {
  const candidates = preview && preview.candidates;
  if (!candidates || !Array.isArray(candidates.rows)) {
    return { rows: [], total: 0, truncated: false, ownerInspectionField: (preview && preview.ownerInspectionField) || null };
  }
  return {
    rows: candidates.rows,
    total: typeof candidates.total === "number" ? candidates.total : candidates.rows.length,
    truncated: Boolean(candidates.truncated),
    ownerInspectionField: (preview && preview.ownerInspectionField) || null,
  };
}

/**
 * Default checkbox selection: new, changed or reinstate (a cancelled booking
 * the yard lists again) rows that are owner
 * inspections (SPEC owner feedback 6 Oct 2026). A row with
 * `ownerInspection: null` (the ITP ledger format — no field in that format
 * identifies owner attendance, see `buildCandidates`'s doc comment) is
 * never knowingly excluded by an owner-attendance test this app cannot
 * make, so it is included by default; `ownerInspection: false` never
 * occurs today (painting rows are always `true`) but is excluded if it
 * ever does. `unchanged`/`retiring` rows are never pre-selected — they are
 * either already imported (never re-written) or a removal the SM must
 * confirm explicitly.
 */
export function defaultImportSelection(rows) {
  return (rows || [])
    .filter((r) => (r.status === "new" || r.status === "changed" || r.status === "reinstate") && r.ownerInspection !== false)
    .map((r) => r.key);
}

/** Whether the "owner inspections only" filter can actually narrow anything for this run's format. */
export function ownerFilterAvailable(rows) {
  return (rows || []).some((r) => r.ownerInspection === true || r.ownerInspection === false);
}

/** Rows visible under the "owner inspections only" toggle; off (or unavailable) shows everything. */
export function filterOwnerOnly(rows, ownerOnly) {
  if (!ownerOnly) return rows || [];
  return (rows || []).filter((r) => r.ownerInspection !== false);
}

/**
 * Whole Plan screen model from the `/api/cockpit/plan` envelope. `today`
 * (ISO date, injected for tests) drives the "today" column highlight and
 * tile chip.
 */
export function shapePlanScreen(envelope, today) {
  const weekStart = envelope && envelope.weekStart;
  const days = weekdayDates(weekStart).map((date) => ({
    date,
    label: WEEKDAY_SHORT[new Date(`${date}T00:00:00Z`).getUTCDay()],
    dayMonth: formatDayShort(date),
    isToday: date === today,
  }));
  // A cancelled booking is dead — schedule.js's cancelPlannedByImport marks
  // it Cancelled rather than deleting it, but the plan grid is a live
  // working view, not an archive, so it is never worth a tile.
  const items = ((envelope && envelope.items) || [])
    .filter((row) => row.scheduleStatus !== "Cancelled")
    .map((row) => shapePlanTile(row, today));
  const itemsByCell = new Map();
  for (const tile of items) {
    if (!days.some((d) => d.date === tile.plannedDate)) continue; // outside Mon-Sun of this week
    const cellKey = `${tile.assignedTo || ""}|${tile.plannedDate}`;
    if (!itemsByCell.has(cellKey)) itemsByCell.set(cellKey, []);
    itemsByCell.get(cellKey).push(tile);
  }
  const lanes = ((envelope && envelope.lanes) || [])
    .map((lane) => ({
      key: lane.upn || "",
      upn: lane.upn || "",
      name: lane.upn ? initialsName(lane.upn) : "Unassigned",
      teamLabel: laneTeamLabel(lane.teams),
      teams: lane.teams || [],
      rosterCodes: lane.rosterTeams || lane.teams || [],
      cells: days.map((d) => itemsByCell.get(`${lane.upn || ""}|${d.date}`) || []),
    }))
    // The Unassigned lane (planView always appends `{upn:"",teams:[]}`) is
    // an administrative placeholder, never a real inspector — show it only
    // when something actually sits in it, matching the artboard, which
    // carries no permanently-empty lane.
    .filter((lane) => lane.upn !== "" || lane.cells.some((cell) => cell.length > 0));
  const unbooked = ((envelope && envelope.unbooked) || []).map(shapeUnbookedRow);
  const fullUnbookedRowsByRef = new Map(unbooked.map((u) => [u.scheduleRef, u.row]));
  // "Booked" reads as "on the calendar" (done + today + booked chips
  // together); "done" is then the completed subset of that same total —
  // an item on hold (Skipped) is deliberately off the calendar, so it is
  // the one chip excluded from both counts.
  const onCalendar = items.filter((t) => t.chipClass !== "mute");
  const doneCount = onCalendar.filter((t) => t.chipClass === "ok").length;
  const needInspectorCount = onCalendar.filter((t) => t.chipText === "needs inspector").length;
  // booked = ready (date + inspector, not yet done); the three counts are disjoint.
  const bookedCount = onCalendar.length - doneCount - needInspectorCount;
  // Plan redesign (owner-approved, DEC-CHAT-20261007-412): planView's own
  // ranked `backlog[]` (re-inspections overdue, then undated rows), paged
  // 20 at a time server-side (`backlogTotal`/`backlogOffset`/`backlogLimit`,
  // PR #1958 §4.3). `backlog` here is this page ONLY — App.jsx accumulates
  // further pages (its own "Show N more" fetch) and re-shapes the
  // concatenated raw rows through this same function.
  const backlog = ((envelope && envelope.backlog) || []).map((row) => shapeBacklogRow(row, fullUnbookedRowsByRef));
  const backlogTotal = typeof (envelope && envelope.backlogTotal) === "number" ? envelope.backlogTotal : backlog.length;
  return {
    weekStart,
    days,
    lanes,
    unbooked,
    backlog,
    backlogTotal,
    backlogOffset: (envelope && envelope.backlogOffset) || 0,
    backlogLimit: (envelope && envelope.backlogLimit) || 20,
    lastImport: shapeLastImport(envelope && envelope.imports, backlogTotal),
    forecast: shapeForecastBand(envelope && envelope.forecast, days),
    repairChecks: shapeRepairChecks(envelope && envelope.repairChecks),
    errors: (envelope && envelope.errors) || [],
    lead: `${bookedCount} booked · ${needInspectorCount} need an inspector · ${doneCount} done · ${backlogTotal} to book · drag to move an inspection to another day or inspector, across projects`,
  };
}

/**
 * The row an upsert sends for a drag drop: the tile's original full row
 * (every field `validateScheduleItem` requires) with only `assignedTo`
 * and/or `plannedDate` overridden — the two `SITE_MANAGER_EDITABLE` fields
 * (SPEC-CIS-SITE-MANAGER-COCKPIT-V3-001 §4.3). Never a partial patch: the
 * backend batch item shape requires every field on every call.
 */
export function buildDragUpsertRow(tile, { assignedTo, plannedDate } = {}) {
  const row = tile.row || {};
  const built = {
    scheduleRef: row.scheduleRef,
    title: row.title,
    category: row.category,
    stage: row.stage,
    inspectionType: row.inspectionType,
    plannedDate: plannedDate !== undefined ? plannedDate : row.plannedDate,
    witnessParties: row.witnessParties || "",
    assignedTo: assignedTo !== undefined ? assignedTo : row.assignedTo || "",
    notes: row.notes || "",
  };
  // validateScheduleItem (azure-functions/cis/shared/validation.js:273-277)
  // rejects a PRESENT sourceInspectionNumber that isn't a positive integer —
  // 0 included. Every ordinary drag row (no source inspection) must omit the
  // field entirely, never send a falsy placeholder.
  if (row.sourceInspectionNumber) built.sourceInspectionNumber = row.sourceInspectionNumber;
  return built;
}

/**
 * The notice reason for one Plan upsert result item, or `null` when it is
 * one of the given `successStatuses` (codex review finding, PR #1963 P2
 * round 2: an HTTP 200 whose per-item result is ITSELF a failure — the
 * real backend status `"error"` with an `errorCode`, shared/schedule.js —
 * previously fell through App.jsx's own status checks into the same
 * silent branch as a genuine success, since only `"invalid"`/`"conflict"`
 * were ever recognised as failures there). Callers special-case the
 * statuses with their own nicer wording (invalid/conflict/duplicate)
 * BEFORE falling back to this — it is the catch-all: any status not in
 * `successStatuses` is a failure, and its own `errorCode` (when the
 * backend sent one) is what gets shown, never swallowed.
 */
export function describePlanUpsertFailure(item, result, successStatuses) {
  if (item && successStatuses.includes(item.status)) return null;
  if (item) return item.errorCode || `unexpected status "${item.status}"`;
  return (result && result.body && (result.body.errorCode || result.body.message)) || `http_${(result && result.status) || 0}`;
}

function cryptoRandomUuidV4() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  // Node's global crypto covers the test runner too (Node 19+); this
  // fallback only matters on an older runtime and is never used in the
  // browser build.
  const bytes = new Uint8Array(16);
  for (let i = 0; i < 16; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = [...bytes].map((b) => b.toString(16).padStart(2, "0"));
  return `${hex.slice(0, 4).join("")}-${hex.slice(4, 6).join("")}-${hex.slice(6, 8).join("")}-${hex.slice(8, 10).join("")}-${hex.slice(10, 16).join("")}`;
}

/**
 * The new row a "Book on calendar" drop posts (SPEC §4.3 booking rule):
 * `scheduleRef = SM-<yyyyMMdd>-<8 hex of a UUID v4>`, carrying the pinned
 * re-inspection's identity forward as `sourceInspectionNumber`.
 */
export function buildBookingRow(pinned, { assignedTo, plannedDate, now = new Date(), uuid } = {}) {
  const yyyyMMdd = now.toISOString().slice(0, 10).replace(/-/g, "");
  const hex8 = (uuid || cryptoRandomUuidV4()).replace(/-/g, "").slice(0, 8);
  const built = {
    scheduleRef: `SM-${yyyyMMdd}-${hex8}`,
    title: pinned.title || "",
    category: pinned.category || "Others",
    stage: pinned.stage || "Rectification",
    inspectionType: pinned.inspectionType || "Witness Point",
    plannedDate,
    witnessParties: "",
    assignedTo: assignedTo || "",
    notes: "",
  };
  // Same rule as buildDragUpsertRow: validateScheduleItem rejects a present
  // sourceInspectionNumber of 0. A booking is only ever built from a pinned
  // overdue re-inspection, which always carries a real inspection number,
  // but this stays defensive rather than trusting the caller.
  const source = pinned.sourceInspectionNumber || pinned.inspectionNumber || 0;
  if (source) built.sourceInspectionNumber = source;
  return built;
}

/**
 * Human-readable text for an `invalid` batch-envelope entry
 * (`[field, reasonCode]` pairs — azure-functions/cis/shared/validation.js,
 * CisScheduleImport §4.3). Names the actual field and reason the server
 * returned rather than assuming every invalid drop is a roster miss — a
 * validation failure on any other field (e.g. sourceInspectionNumber) must
 * not be reported as "not on the roster".
 */
export function describePlanInvalid(action, scheduleRef, code, assignedTo, errors) {
  const [field, reason] = (errors && errors[0]) || [];
  if (field === "assignedTo" && reason === "inspector_not_on_roster") {
    return `${initialsName(assignedTo)} is not on the ${code} roster. Assign someone from the ${code} team, or leave it unassigned.`;
  }
  if (field === "assignedTo") {
    return `Could not ${action} ${scheduleRef}: ${assignedTo} is not on the ${code} roster (${reason || "invalid"}).`;
  }
  if (field === "sourceInspectionNumber" && reason === "source_not_found") {
    return `Could not ${action} ${scheduleRef}: source inspection not found (project ${code}).`;
  }
  return `Could not ${action} ${scheduleRef}: ${field || "request"} — ${reason || "invalid"} (project ${code}).`;
}

/**
 * NCR screen — shapes the per-project `GET /api/projects/{code}/ncrs` rows
 * (azure-functions/cis/shared/ncr.js `rowFromSpNcr`) into the four tabs
 * mockups/cockpit-v3/NCR.dc.html renders (§4.4: To decide = Recorded, Raised
 * awaiting yard = Open, Answered our move = Answered, Closed = Closed +
 * Cancelled). There is no single cross-project NCR read (unlike Today/Plan);
 * the cockpit calls the existing per-project endpoint once per `sm:` project
 * and merges here, same errors[] partial-failure discipline as Today/Plan.
 *
 * `GET /api/projects/{code}/ncrs` (`CisNcrsQuery`) carries a real `photoCount`
 * per row, computed server-side from `NcrPhotos-<CODE>` in one batched read
 * (never per-row) — see `azure-functions/cis/shared/ncr-photos.js`
 * `countNcrPhotosByProject`/`withPhotoCounts`. The artboard's "N photos" text
 * is rendered from that real count.
 */

export const NCR_TAB_STATUSES = {
  toDecide: ["Recorded"],
  raised: ["Open"],
  answered: ["Answered"],
  closed: ["Closed", "Cancelled"],
};

/**
 * The date shown on an NCR row differs by tab: the artboard's only rendered
 * tab (To decide) shows the date the inspector recorded it (`raisedDate`).
 * The other three tabs have no artboard sample to match, so this picks the
 * date that actually means something for that state — when it reached the
 * yard, when the yard answered, or when it was last touched — rather than
 * repeating `raisedDate` for a row that has moved on.
 */
export function ncrDateLabel(row) {
  if (row.status === "Recorded") return formatDayShort(row.raisedDate);
  if (row.status === "Open") return row.raisedToYardAtUtc ? formatDayShort(row.raisedToYardAtUtc.slice(0, 10)) : formatDayShort(row.raisedDate);
  if (row.status === "Answered") return row.yardAnswerDate ? formatDayShort(row.yardAnswerDate) : formatDayShort(row.raisedDate);
  return row.lastChangedUtc ? formatDayShort(row.lastChangedUtc.slice(0, 10)) : formatDayShort(row.raisedDate);
}

/** One NCR register row shaped for the list/detail pane. */
export function shapeNcrRow(code, row) {
  return {
    key: `${code}-${row.ncrNumber}`,
    code,
    ncrNumber: row.ncrNumber,
    subId: `D${row.ncrNumber}`,
    title: row.title || "",
    location: row.location || "",
    description: row.description || "",
    status: row.status,
    // The inspector who logged the NCR (createdByUpn) — the person the
    // artboard's "who" column names, not DutyDept (which is which discipline
    // is responsible for the fix, a different field entirely).
    who: initialsName(row.createdByUpn),
    dateLabel: ncrDateLabel(row),
    photoCount: Number(row.photoCount || 0),
    etag: row.etag,
    raiseRunId: row.raiseRunId || "",
    returnNote: row.returnNote || "",
    returnedAtUtc: row.returnedAtUtc || "",
    closureNote: row.closureNote || "",
    yardAnswer: row.yardAnswer || "",
    yardAnswerDate: row.yardAnswerDate || "",
    raisedToYardAtUtc: row.raisedToYardAtUtc || "",
    raisedDate: row.raisedDate || "",
    // SPEC-CIS-NCR-RETURN-RESUBMIT-001 §4.7: a Recorded row the SM returned
    // and the inspector has not yet resubmitted.
    awaitingResubmit: Boolean(row.awaitingResubmit),
    openReturnId: Number(row.openReturnId || 0),
    raw: row,
  };
}

/**
 * The status chip label for an NCR row (§4.7): a `Recorded` row still
 * awaiting resubmit reads "Returned to inspector" instead of the bare
 * `Recorded` status — the existing `Recorded`/`Open`/`Answered`/`Closed`/
 * `Cancelled` transition matrix is unchanged; this is a label only.
 */
export function ncrStatusLabel(row) {
  if (row.status === "Recorded" && row.awaitingResubmit) return "Returned to inspector";
  return row.status;
}

/**
 * Cache key for an NCR row's return-history fetch (NCR.jsx, codex review
 * finding #3, PR #1950). MUST include `etag`, not just `key`: every
 * mutating action on the row (another return, a resubmit, a description
 * save) re-fetches the row with a fresh etag via loadNcrs() — keying the
 * cache on `key` alone kept showing an earlier return's history (including
 * a stale "Open" status on a row a later return had already superseded)
 * after the row had moved on. Etag changing is exactly the signal this
 * spec's own invariants (I3/AC-18) already treat as authoritative for
 * "has this row changed" — reusing it here makes the cache self-invalidate
 * on every write, with no separate invalidation call needed.
 */
export function historyCacheKeyFor(row) {
  return row ? `${row.key}:${row.etag}` : null;
}

/**
 * Whole NCR screen model from `{ projects: [{ code, rows }], errors }`,
 * where `rows` is one project's raw `GET /api/projects/{code}/ncrs` array.
 * Rows within a tab sort oldest-recorded-first (the artboard's own order),
 * by NCR number as the tiebreak.
 */
export function shapeNcrScreen(projects, errors) {
  const byTab = { toDecide: [], raised: [], answered: [], closed: [] };
  for (const project of projects || []) {
    for (const row of project.rows || []) {
      const shaped = shapeNcrRow(project.code, row);
      for (const [tab, statuses] of Object.entries(NCR_TAB_STATUSES)) {
        if (statuses.includes(row.status)) byTab[tab].push(shaped);
      }
    }
  }
  const byDate = (a, b) => (a.dateLabel < b.dateLabel ? -1 : a.dateLabel > b.dateLabel ? 1 : a.ncrNumber - b.ncrNumber);
  for (const tab of Object.keys(byTab)) byTab[tab].sort(byDate);
  return { tabs: byTab, errors: errors || [] };
}

/** UUID v4 for a new `raiseId`, generated once per user action and reused on retry. */
export function newRaiseId() {
  return cryptoRandomUuidV4();
}

/**
 * The `{ etag, description }` PATCH body for saving an edited NCR wording —
 * `validateNcrPatch` (azure-functions/cis/shared/validation.js) requires
 * only `etag` plus at least one editable field, and `description` is the
 * only field this screen ever writes before a raise.
 */
export function buildDescriptionPatch(row, description) {
  return { etag: row.etag, description };
}

/** The `{ etag, returnNote }` PATCH body for "Return to inspector" (§4.4: a field-precondition write, not a status transition). */
export function buildReturnPatch(row, returnNote) {
  return { etag: row.etag, returnNote };
}

/** The `{ etag, status: "Cancelled", closureNote }` PATCH body for "Dismiss" (§4.4 Recorded→Cancelled, precondition: closureNote non-empty). */
export function buildDismissPatch(row, closureNote) {
  return { etag: row.etag, status: "Cancelled", closureNote };
}

/**
 * The `POST /api/cockpit/ncrs/raise` body for a selection of To-decide rows.
 * `edits` is `{ [rowKey]: description }` for any row whose wording was
 * changed in the preview pane before raising; only a description that
 * actually differs from the row's current value is sent, since
 * `validateRaiseBody` refuses an empty string but a PATCH was already applied
 * for the actual save (§4.5 step 2), so this raise body only needs to carry
 * an edit the caller wants applied atomically with the raise itself when the
 * separate PATCH has not yet round-tripped a fresh etag.
 */
export function buildRaiseBody(rows, raiseId, edits = {}) {
  return {
    raiseId,
    items: rows.map((row) => {
      const item = { code: row.code, ncrNumber: row.ncrNumber, etag: row.etag };
      const edited = edits[row.key];
      if (edited !== undefined && edited !== row.description) item.description = edited;
      return item;
    }),
  };
}

/** The `{ outcome }` body for `POST /api/cockpit/ncrs/raise/{runId}/resolve`. */
export function buildResolveBody(outcome) {
  return { outcome };
}

/**
 * Human-readable text for one project's raise outcome
 * (`{ code, status: sent|duplicate|failed|unknown, error? }`, §4.5 step 8).
 * Never empty — every project entry gets a visible line, including the
 * ones that succeeded.
 */
export function describeRaiseOutcome(project) {
  if (project.status === "sent") return `${project.code}: sent`;
  if (project.status === "duplicate") return `${project.code}: already sent (duplicate)`;
  if (project.status === "unknown") return `${project.code}: send state unknown (${project.error || "unknown_send_state"}) — resolve below`;
  const error = project.error || "unknown error";
  const advice = RAISE_FAILURE_ADVICE[project.error];
  return advice ? `${project.code}: failed (${error}) — ${advice}` : `${project.code}: failed (${error})`;
}

/**
 * What a failed raise means for the user. Every entry says whether the NCR
 * changed and what to do next; the raw code stays in the line for support.
 */
const RAISE_FAILURE_ADVICE = Object.freeze({
  mail_unavailable: "the mail was not sent and the NCR is unchanged. Tap Raise again to retry.",
  transient_upstream: "no answer from the server. The mail may or may not have gone; check the NCR list before raising again.",
  test_project_mail_restricted: "a test project can only mail the internal test mailbox. Nothing was sent and the NCR is unchanged.",
  external_send_blocked: "the yard address is outside the allowed mail domains. Nothing was sent and the NCR is unchanged.",
  no_yard_recipient: "this project has no yard recipient set. Nothing was sent and the NCR is unchanged.",
  send_rate_limited: "the daily mail limit is reached. Nothing was sent; try again tomorrow.",
  stale_etag: "the NCR changed since you opened it. Reload and raise again.",
  attachments_too_large: "the register file is too large to mail. Nothing was sent.",
  raise_failed: "something went wrong before the mail was sent. The NCR is unchanged. Tap Raise again to retry.",
});

/**
 * One-flight guard for the Raise button. A second tap while a raise is in
 * flight is refused, and every tap of one user action reuses one raiseId
 * (the server's idempotency key), so a double tap can never be two raises.
 * The id is kept after a non-terminal outcome so a retry is the same raise.
 */
export function createRaiseGate(makeId = newRaiseId) {
  let id = null;
  let inFlight = false;
  return {
    begin() {
      if (inFlight) return null;
      inFlight = true;
      if (!id) id = makeId();
      return id;
    },
    end(terminal) {
      inFlight = false;
      if (terminal) id = null;
    },
  };
}

/**
 * "Export NCR register" scope (EP-CIS-004, DEC-CHAT-20261010-404): the
 * distinct project codes with any NCR on screen, sorted. The screen's
 * `model.tabs` is already filtered to the top bar's selected project(s)
 * (App.jsx builds it through filterByProjectCodes), so under "All projects"
 * this is every sm: project with NCRs — one export per project, the same
 * per-project pattern Raise uses (CisNcrExport is a per-project route).
 */
export function ncrExportCodes(tabs) {
  const base = tabs || {};
  const codes = new Set();
  for (const rows of [base.toDecide, base.raised, base.answered, base.closed]) {
    for (const row of rows || []) codes.add(row.code);
  }
  return [...codes].sort();
}

/**
 * Runs one register export per project code through `exportOne` (App.jsx
 * wires it to client.js#exportNcrs — POST /api/projects/{code}/ncrs/exports,
 * the existing CisNcrExport endpoint; the server applies its own Open +
 * Answered default). Returns one result per code, in order:
 * `{ code, status: "done", itemCount, photoCount, fileUrl, gaps }` on 200,
 * `{ code, status: "failed", error }` otherwise — same error naming as the
 * screen's load path (`errorCode` or `http_<status>`). Never rejects:
 * client.js methods always resolve an envelope.
 */
export async function runNcrRegisterExports(codes, exportOne) {
  return Promise.all(
    (codes || []).map(async (code) => {
      const result = await exportOne(code);
      if (result && result.ok && result.body) {
        return {
          code,
          status: "done",
          itemCount: result.body.itemCount,
          photoCount: result.body.photoCount,
          fileUrl: result.body.fileUrl,
          gaps: Array.isArray(result.body.gaps) ? result.body.gaps : [],
        };
      }
      return {
        code,
        status: "failed",
        error: (result && result.body && result.body.errorCode) || `http_${(result && result.status) || 0}`,
      };
    })
  );
}

/** One export result's line — counts + bundling gaps on success (the capture app's old wording, cockpit-cased), the named failure otherwise. */
export function describeNcrExportOutcome(result) {
  if (result.status === "done") {
    const gapCount = (result.gaps || []).length;
    const gapNote = gapCount ? ` · ${gapCount} photo${gapCount === 1 ? "" : "s"} could not be bundled — see the run report` : "";
    return `${result.itemCount} NCRs · ${result.photoCount} photos exported${gapNote}`;
  }
  return `Export failed — ${result.error || "unknown error"}`;
}

/**
 * Weekly report screen — shapes the per-project `GET /api/cockpit/reports`
 * entry (`{ code, readiness: { lines, readinessHash, allGreen, weekStart },
 * reports: [reportRow, ...] }`, azure-functions/cis/shared/report-state.js
 * `reportRow` + `shared/cockpit.js` `readinessView`) into the Report screen's
 * model (mockups/cockpit-v3/Report.dc.html §4.6), same live/fixture-through-
 * one-pipeline discipline as Today/Plan/NCR.
 *
 * The artboard's readiness checklist shows four illustrative lines
 * ("Inspections have results", "Inspections without photos", "NCRs updated
 * this week", "Progress entered") with an X/Y or item-list format. The real
 * contract (§4.6 readiness table) carries exactly three lines
 * (`inspection_results`, `inspection_photos`, `ncr_decisions`), each only a
 * count and a green flag — no denominator, no item list, and the third
 * line's real meaning is "NCRs still Recorded" (any age), not "updated this
 * week". There is no fourth "Progress entered" field anywhere in the data
 * contract (§5) at all. This module renders the three real lines with their
 * real semantics and omits the fourth rather than inventing one; see
 * v-artboard.deviations.json for the accepted text deviations this causes.
 */

/** Static per-line label, independent of red/green state — REPORT_LINE_LABELS[id] names what the line checks; READINESS_LABELS[id](n) (above) names the count when it is red. */
export const REPORT_LINE_LABELS = {
  inspection_results: "Inspections have results",
  inspection_photos: "Inspections without photos",
  ncr_decisions: "NCRs awaiting decision",
};

/** Owner retest 3: the results line is named by state, not as a question. */
export function reportLineLabel(id, count) {
  if (id === "inspection_results") return count > 0 ? `Results missing · ${count}` : "Results recorded";
  return REPORT_LINE_LABELS[id] || id;
}

/**
 * The three real readiness lines, shaped for the checklist (icon colour +
 * label + count) — never the artboard's fourth "Progress entered" line,
 * which no backend field carries. `items` (readinessView, shared/cockpit.js
 * — additive, never affects readinessHash) is carried through so a red
 * `ncr_decisions` line can list and link to the real blocking NCR(s)
 * instead of only showing a count (owner: "show in the UI which NCRs
 * block and link to them, so a site manager can resolve them").
 */
export function shapeReadinessChecklist(readiness) {
  const lines = (readiness && readiness.lines) || [];
  return lines.map((line) => ({
    id: line.id,
    label: reportLineLabel(line.id, Number(line.count || 0)),
    green: Boolean(line.green),
    count: Number(line.count || 0),
    items: line.items || [],
  }));
}

/**
 * "W35" from an ISO Monday date string, for the archive row and card
 * header — prefixed with the week's own calendar year ("2024 W44") whenever
 * that year differs from `currentYear` (defaults to the real current year;
 * tests inject one for a deterministic answer). Real live data (SS411 #9:
 * WeekStart 2024-10-28) has reports for a week almost two years back sitting
 * next to this week's — a bare "W44" reads as recent without the year.
 */
export function reportWeekLabel(weekStart, currentYear = new Date().getUTCFullYear()) {
  const n = isoWeekNumber(weekStart);
  if (!n) return "";
  const parsed = Date.parse(`${weekStart}T00:00:00Z`);
  const weekYear = Number.isFinite(parsed) ? new Date(parsed).getUTCFullYear() : null;
  return weekYear !== null && weekYear !== currentYear ? `${weekYear} W${n}` : `W${n}`;
}

/** "Fri 28 Aug 09:12" — formatDay's weekday+day+month plus a UTC HH:MM. There is no per-project IANA timezone field on any cockpit response (`CIS_PROJECT_TIMEZONE__<CODE>` is a Function App setting, never returned to the client), so this renders the stamp's UTC clock time rather than converting to a zone the client cannot know. */
export function formatDayTime(value) {
  const parsed = Date.parse(value);
  if (!Number.isFinite(parsed)) return String(value || "");
  const d = new Date(parsed);
  const hh = String(d.getUTCHours()).padStart(2, "0");
  const mm = String(d.getUTCMinutes()).padStart(2, "0");
  return `${formatDay(value)} ${hh}:${mm}`;
}

// --- Weekly report screen v2 (DEC-CHAT-20261010-403): display helpers ---------

/** "09:26" -- the UTC clock time of an ISO stamp ("" if unparseable). */
export function formatClock(value) {
  const parsed = Date.parse(value);
  if (!Number.isFinite(parsed)) return "";
  const d = new Date(parsed);
  return `${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")}`;
}

/**
 * A stamp on the day the screen is looking at reads as just the clock
 * ("09:26"); any other day carries its weekday and date ("Fri 9 Oct 14:35").
 */
export function formatStamp(value, today) {
  const parsed = Date.parse(value);
  if (!Number.isFinite(parsed)) return "";
  const day = new Date(parsed).toISOString().slice(0, 10);
  return today && day === today ? formatClock(value) : formatDayTime(value);
}

/** "5 – 11 Oct" / "28 Sep – 4 Oct" (+ " 2026" with `year`; both years when they differ). */
export function formatRangeText(start, end, { year = false } = {}) {
  const a = Date.parse(`${start}T00:00:00Z`);
  const b = Date.parse(`${end}T00:00:00Z`);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return "";
  const da = new Date(a);
  const db = new Date(b);
  const sameMonth = da.getUTCFullYear() === db.getUTCFullYear() && da.getUTCMonth() === db.getUTCMonth();
  const left = sameMonth ? `${da.getUTCDate()}` : `${da.getUTCDate()} ${MONTH[da.getUTCMonth()]}${da.getUTCFullYear() !== db.getUTCFullYear() && year ? ` ${da.getUTCFullYear()}` : ""}`;
  const right = `${db.getUTCDate()} ${MONTH[db.getUTCMonth()]}`;
  return `${left} – ${right}${year ? ` ${db.getUTCFullYear()}` : ""}`;
}

/** The Monday-Sunday span of a week: "5 – 11 Oct". */
export function weekRangeText(weekStart, opts) {
  const start = Date.parse(`${weekStart}T00:00:00Z`);
  if (!Number.isFinite(start)) return "";
  return formatRangeText(weekStart, new Date(start + 6 * DAY_MS).toISOString().slice(0, 10), opts);
}

/** "40 days · covers W36 – W41" -- the helper next to the From/To inputs. */
export function periodSpanText(periodStart, periodEnd) {
  const a = Date.parse(`${periodStart}T00:00:00Z`);
  const b = Date.parse(`${periodEnd}T00:00:00Z`);
  if (!Number.isFinite(a) || !Number.isFinite(b) || b < a) return "";
  const days = Math.round((b - a) / DAY_MS) + 1;
  const w1 = isoWeekNumber(mondayOf(periodStart));
  const w2 = isoWeekNumber(mondayOf(periodEnd));
  return `${days} day${days === 1 ? "" : "s"} · covers ${w1 === w2 ? `W${w1}` : `W${w1} – W${w2}`}`;
}

/** "you" for the signed-in user, else "A. Lund"-style initials name. */
export function whoLabel(upn, me) {
  const value = String(upn || "").trim().toLowerCase();
  if (!value) return "";
  if (me && value === String(me).trim().toLowerCase()) return "you";
  return initialsName(value);
}

/** The file name at the end of a URL path, decoded ("" if none). */
export function fileNameFromUrl(url) {
  try {
    const parts = new URL(String(url || ""), "https://x.invalid").pathname.split("/").filter(Boolean);
    return decodeURIComponent(parts[parts.length - 1] || "");
  } catch {
    return "";
  }
}

const REPORT_STATE_CHIP = {
  Draft: { text: "draft", class: "warn" },
  Approved: { text: "approved", class: "acc" },
  Issued: { text: "published", class: "ok" },
};

/** The version with the latest `createdAtUtc` — used as this report's "generated" timestamp; `reportRow` itself carries no separate generatedAtUtc field, only the confirm/approve/issue stamps. */
export function latestReportVersion(report) {
  const versions = (report && report.versions) || [];
  const best = versions.reduce((b, v) => (!b || String(v.createdAtUtc || "") > String(b.createdAtUtc || "") ? v : b), null);
  // History rows arrive without their version list (read lazily); the cheap
  // latestVersion summary carries the same "generated" timestamp.
  if (!best && report && report.latestVersion && report.latestVersion.generatedAt) {
    return { versionId: "", createdAtUtc: report.latestVersion.generatedAt, url: report.latestVersion.url || "" };
  }
  return best;
}

/**
 * One archive row (mockups/cockpit-v3/Report.dc.html archive list): one
 * primary "Open in Word" button for the current version (`docxUrl`, opens
 * directly in Word — the artboard's own single button), plus, only when the
 * row has more than one version, a small secondary "vN" link per
 * `versions[]` entry (via the cockpit API's own version-content route,
 * CisCockpitReports `readVersionContent`) so every version stays
 * independently openable (§4.6) without duplicating the primary button — see
 * v-artboard.deviations.json for the accepted per-row deviation this adds on
 * a multi-version row.
 */
export function shapeReportArchiveRow(report, currentYear = new Date().getUTCFullYear()) {
  // `reportRow` (shared/report-state.js) carries ReportStatus verbatim from
  // the list item as `f.ReportStatus || ""`. Real, currently-live
  // WeeklyReports-<CODE> data (checked via Graph, read-only, 2026-09-27:
  // SS411 #1, SS411S #1, SS418 #1/#2) confirms a REAL, positive-reportNumber
  // report can carry a null/empty ReportStatus — these predate report-state.
  // js's generation write always stamping "Draft" and were never backfilled.
  // At this point in the pipeline `isRealReportRow` has already excluded
  // every reportNumber<=0 row, so an empty status here is never "no report",
  // it is always this one real, named legacy state — "legacy" says that
  // plainly instead of the opaque, unexplained "unknown" the owner flagged
  // on real numbered reports (never a generic fallback for any OTHER
  // unrecognized status string, which still shows the real value verbatim).
  const chip = REPORT_STATE_CHIP[report.reportStatus] ||
    (report.reportStatus ? { text: String(report.reportStatus).toLowerCase(), class: "mute" } : { text: "legacy", class: "mute" });
  const versions = report.versions || [];
  const versionCount = Math.max(versions.length, Number(report.versionCount) || 0);
  // Owner retest 4: plainly "vN" (the generation number) next to "generated
  // <date time>" — no "of M" (M counts stored file versions, a different thing).
  const versionNumber = Number(report.generationCount) || versionCount || 1;
  const versionLabel = `v${versionNumber}`;
  const latestForLabel = latestReportVersion(report);
  const generatedLabel = latestForLabel && latestForLabel.createdAtUtc ? `generated ${formatDayTime(latestForLabel.createdAtUtc)}` : "";
  let detail;
  if (report.reportStatus === "Issued" && report.issuedAtUtc) {
    detail = `on project site ${formatDayShort(report.issuedAtUtc.slice(0, 10))}`;
  } else if (report.reportStatus === "Approved" && report.approvedAtUtc) {
    detail = `approved ${formatDayShort(report.approvedAtUtc.slice(0, 10))} · not yet published`;
  } else {
    const latest = latestReportVersion(report);
    detail = latest && latest.createdAtUtc
      ? ""
      : report.dataConfirmedAtUtc
      ? `confirmed ${formatDay(report.dataConfirmedAtUtc)}`
      : "";
  }
  // DEC-CHAT-20260928-202: a stamped report (report-state.js always writes
  // ReportStatus at generation) with no LlmRunId carries no model prose —
  // either the site manager asked for none, or the model was unavailable;
  // either way the archive row says so. A truly legacy row (reportStatus
  // empty, predates report-state.js stamping "Draft") is left alone — its
  // own "legacy" chip already explains the missing fields.
  if (report.reportStatus && !report.llmRunId) {
    detail = detail ? `${detail} · no prose` : "no prose";
  }
  const primaryUrl = report.docxUrl || (versions[0] && versions[0].url) || (report.latestVersion && report.latestVersion.url) || "";
  // SPEC-CIS-REPORT-PDF-PUBLISH-001 4.6: "Open PDF" for an Approved or Issued
  // row whose PDF exists; "PDF not created" + Retry for an Approved row whose
  // archive row says it failed. A row without `pdfStatus` (an older backend)
  // shows neither. "Open in Word" stays for every version.
  const pdfStatus = report.pdfStatus || "";
  const hasPdfState = report.reportStatus === "Approved" || report.reportStatus === "Issued";
  const pdfLinks = pdfStatus && hasPdfState && report.pdfUrl ? [{ key: "pdf", label: "Open PDF", url: report.pdfUrl }] : [];
  const openLinks = [...pdfLinks, ...(primaryUrl ? [{ key: "current", label: "Open in Word", url: primaryUrl }] : [])];
  const versionLinks = versions.length > 1 ? versions.filter(Boolean).map((v, i, all) => ({ key: v.versionId || `v${i}`, label: `v${all.length - i}`, url: v.url || "" })) : [];
  // SPEC-CIS-REPORT-CUSTOM-PERIOD-001 section 4.2: a custom row shows its
  // period label and carries a "Covers ..." tag; a week row is unchanged.
  const isCustom = report.periodKind === "custom";
  const periodLabel = isCustom ? report.periodLabel || formatPeriodLabel(report.periodStart, report.periodEnd) : "";
  return {
    key: `${report.reportNumber}`,
    reportNumber: report.reportNumber,
    weekLabel: isCustom ? periodLabel : reportWeekLabel(report.weekStart, currentYear),
    isCustom,
    periodStart: report.periodStart || "",
    periodEnd: report.periodEnd || "",
    periodLabel,
    coversTag: isCustom ? `Covers ${formatCoversSpan(report.periodStart, report.periodEnd)}` : "",
    weekStart: report.weekStart,
    versionLabel,
    versionNumber,
    generatedLabel,
    versionCount,
    // Set only while the list is not loaded and there is more than one
    // version: the row fetches it on expand.
    versionsUrl: !versions.length && versionCount > 1 ? report.versionsUrl || "" : "",
    chipText: chip.text,
    chipClass: chip.class,
    detail,
    canPublish: report.reportStatus === "Approved",
    openLinks: openLinks.length ? openLinks : [{ key: "none", label: "Open in Word", url: "" }],
    pdfStatus,
    pdfUrl: report.pdfUrl || "",
    canRetryPdf: report.reportStatus === "Approved" && pdfStatus === "failed",
    versionLinks,
    etag: report.etag,
    raw: report,
    ...shapeReportRowV2(report, { isCustom, versions, versionCount, versionLinks, currentYear, openLinks, primaryUrl, pdfStatus, noProse: Boolean(report.reportStatus && !report.llmRunId) }),
  };
}

/**
 * When the file was generated and when it was last edited in Word, from its
 * stored versions. Generation writes the first file version at or after the
 * (re)confirmation; every later save is an edit. A row whose versions are not
 * loaded has only its last-modified time.
 */
export function reportTimes(report) {
  const versions = ((report && report.versions) || []).filter((v) => v && Number.isFinite(Date.parse(v.createdAtUtc)));
  if (!versions.length) {
    const lm = (report && report.latestVersion && report.latestVersion.generatedAt) || "";
    return { generatedAt: lm, editedAt: "" };
  }
  const sorted = [...versions].sort((a, b) => Date.parse(a.createdAtUtc) - Date.parse(b.createdAtUtc));
  const confirmedMs = Date.parse((report && report.dataConfirmedAtUtc) || "");
  const gen = sorted.find((v) => !Number.isFinite(confirmedMs) || Date.parse(v.createdAtUtc) >= confirmedMs) || sorted[0];
  const last = sorted[sorted.length - 1];
  const edited = last !== gen && Date.parse(last.createdAtUtc) - Date.parse(gen.createdAtUtc) > 2 * 60 * 1000;
  return { generatedAt: gen.createdAtUtc, editedAt: edited ? last.createdAtUtc : "" };
}

const STATUS_CHIP_V2 = {
  Draft: { text: "Draft", tone: "draft" },
  Approved: { text: "Approved", tone: "approved" },
  Issued: { text: "Published", tone: "published" },
};

/** The v2 table row fields (design Main.dc.html "Reports for <code>"): additive to the archive row. */
function shapeReportRowV2(report, { isCustom, versions, versionCount, versionLinks, currentYear, openLinks, primaryUrl, pdfStatus, noProse }) {
  const times = reportTimes(report);
  const chip = STATUS_CHIP_V2[report.reportStatus] || { text: report.reportStatus ? String(report.reportStatus) : "Legacy", tone: "legacy" };
  const displayVersion = Math.max(versionCount, Number(report.generationCount) || 0, 1);
  const periodText = isCustom
    ? formatRangeText(report.periodStart, report.periodEnd, { year: true })
    : `${reportWeekLabel(report.weekStart, currentYear)} · ${weekRangeText(report.weekStart)}`;
  const noteParts = [];
  if (report.reportStatus === "Draft" && times.editedAt) noteParts.push(`edited in Word ${formatClock(times.editedAt)}`);
  // The site link is its own button in the Actions grid; the note is the date only.
  if (report.reportStatus === "Issued") noteParts.push(report.issuedAtUtc ? `published ${formatDayShort(String(report.issuedAtUtc).slice(0, 10))}` : "published");
  if (noProse) noteParts.push("no prose");
  const pdfLink = openLinks.find((l) => l.key === "pdf");
  return {
    publishedNumber: normalisePublishedNumber(report.publishedNumber),
    // The label the table shows: never the internal counter.
    reportText: reportLabel(report.reportStatus, report.publishedNumber, ""),
    reportRef: reportRef(report.reportStatus, report.publishedNumber),
    statusText: chip.text,
    statusTone: chip.tone,
    periodText,
    displayVersion,
    generatedText: times.generatedAt ? formatDayTime(times.generatedAt) : "",
    generatedAt: times.generatedAt,
    editedAt: times.editedAt,
    note: noteParts.join(" · "),
    isDraft: report.reportStatus === "Draft",
    isApproved: report.reportStatus === "Approved",
    isPublished: report.reportStatus === "Issued",
    wordUrl: primaryUrl,
    publishedUrl: report.publishedUrl || "",
    pdfOpenUrl: pdfLink ? pdfLink.url : "",
    pdfFailed: report.reportStatus === "Approved" && pdfStatus === "failed",
    // SPEC-CIS-REPORT-SCHEDULED-PUBLISH-001 section 6: null when nothing changes on screen.
    schedule: shapeSchedule(report),
    // Earlier versions only (the newest is the row itself); [] while not loaded.
    historyLinks: versions.length > 1
      ? versionLinks.slice(1).map((l, i) => ({ ...l, when: formatDayTime((versions.filter(Boolean)[i + 1] || {}).createdAtUtc) }))
      : [],
    historyCount: Math.max(displayVersion - 1, 0),
    hasHistory: displayVersion > 1,
  };
}

/**
 * Owner retest 4: the current report version number for a project's displayed
 * week (0 when none exists yet). Used to notice that a Generate produced a new
 * version, including when the server generates it asynchronously.
 */
export function projectWeekVersion(project) {
  const rows = (project && project.archive) || [];
  const row = rows.find((r) => (project.period
    ? r.isCustom && r.periodStart === project.period.periodStart && r.periodEnd === project.period.periodEnd
    : !r.isCustom && r.weekStart === project.weekStart));
  return row ? row.versionNumber || 0 : 0;
}

/**
 * One project's card. `mode` picks which top block the screen renders:
 * "confirm" (no report exists yet for the requested week — readiness
 * checklist + Confirm data complete), "draft" (a report exists, not yet
 * approved — confirmation/generation summary + Approve/Regenerate),
 * "approved" (awaiting publish — Reopen; Publish itself lives on the
 * archive row, since a later week's report can be Draft while an earlier
 * week's stays Approved-unpublished, per the artboard's SS411 sample) or
 * "issued" (published; nothing left to do at the top).
 */
// The "Confirm data complete" helper text (mockups/cockpit-v3/Report.dc.html):
// disabled state names the specific items still outstanding ("the two are
// listed on Today"); ready state names the report number generation will
// allocate ("Generates #40..."). Neither is a field this screen has —
// readinessView (azure-functions/cis/shared/cockpit.js) carries counts only,
// no item list, and a report number is allocated only at generation time
// (report.js:151-190, max+1 per project) — never predicted in advance. The
// real, generic text below replaces both; see v-artboard.deviations.json.
const CONFIRM_HELPER_BLOCKED = "Unlocks when every line is green. The capture app warned the inspector on save; any items are listed on Today.";
const CONFIRM_HELPER_READY = "It stays a draft until you approve it.";

/**
 * A real generated report always carries a positive `reportNumber`
 * (resolveReportNumber, shared/report.js: max+1 per project, stamped at
 * generation) — `reportRow` (shared/report-state.js) only defaults it to 0
 * when the `ReportNumber` field is empty on the list item it read, which
 * happens for a WeeklyReports-<CODE> library item this generation pipeline
 * never wrote (a template, a folder, or another non-report file living in
 * the same document library). Filtering these out of the archive is not
 * inventing anything about them — it is declining to show a non-report as
 * if it were one (owner-visible "#0 v200 of 200 · unknown" rows traced to
 * exactly this).
 */
function isRealReportRow(report) {
  // Withdrawn (owner decision 1 Oct 2026: reversible removal from views,
  // never a delete — SPEC-CIS-COCKPIT-E2E-TEST-ENV-001 §10 withdraw action)
  // is excluded the same way a non-report row is: it never renders, current
  // or archived, but the underlying row and document are untouched.
  return Number(report && report.reportNumber) > 0 && report.reportStatus !== "Withdrawn";
}

/**
 * Non-blocking reminder list (DEC-CHAT-20260930-203): older `Recorded` NCRs
 * that no longer block this week's report — `readiness.reminders` (shared/
 * cockpit.js readinessView, additive, never part of readinessHash). Shaped
 * the same way a blocking `ncr_decisions` item is (`ncrNumber`/`title`) plus
 * `raisedDate` for the reminder's own display; defaults to `[]` so a fixture
 * or an older server response with no `reminders` field renders nothing,
 * never an error.
 */
export function shapeReportReminders(readiness) {
  const reminders = (readiness && readiness.reminders) || [];
  return reminders.map((r) => ({ ncrNumber: r.ncrNumber, title: r.title || "", raisedDate: r.raisedDate || "" }));
}

export function shapeReportProject({ code, readiness, reports, generationInProgress, scheduleDefault }, currentYear = new Date().getUTCFullYear()) {
  const realReports = (reports || []).filter(isRealReportRow);
  const weekStart = readiness && readiness.weekStart;
  // SPEC-CIS-REPORT-CUSTOM-PERIOD-001: a custom period view shows the custom
  // report with exactly this period; a week view shows the WEEK report of the
  // week -- a custom row is never a week report (identity by PeriodKind), even
  // when its WeekStart (= its periodStart) happens to be that Monday.
  const period = readiness && readiness.periodKind === "custom"
    ? { periodStart: readiness.periodStart, periodEnd: readiness.periodEnd }
    : null;
  const current = (period
    ? realReports.find((r) => r.periodKind === "custom" && r.periodStart === period.periodStart && r.periodEnd === period.periodEnd)
    : realReports.find((r) => r.periodKind !== "custom" && r.weekStart === weekStart)) || null;
  // A custom report that covers this week (any non-Withdrawn status): its
  // number and span, for the "Covered by report N" line. Only for a week view.
  const coveredByCustom = !period && !current
    ? (((readiness && readiness.coveredBy) || []).find((c) => c.periodKind === "custom") || null)
    : null;
  const allGreen = Boolean(readiness && readiness.allGreen);
  // A legacy report (real, positive reportNumber, null/empty ReportStatus —
  // see shapeReportArchiveRow's "legacy" chip) is neither Draft nor Issued.
  // It used to fall into the final `else` ("issued" — Published for this
  // week, no action at all), which is wrong: real live data (SS411 #1,
  // SS411S #1, SS418 #1/#2, Graph read-only 2026-09-27) has legacy reports
  // that were never published. It is also NOT draft-mode: applyReportAction
  // (azure-functions/cis/shared/report-state.js:329-336) requires
  // `status === "Draft"` to approve — every legacy row's own Approve click
  // would 422 invalid_transition, proven in shape.report.legacy-action.test.js
  // against the real function. A legacy report is read-only in this screen:
  // its own top block states that and offers no action; the archive row
  // below still opens the real DOCX and every real version (canPublish is
  // separately gated on reportStatus === "Approved", already false here).
  // Explicit allowlist, never an else-defaults-to-issued fallthrough: a
  // status this screen doesn't recognize (including "Withdrawn", which
  // isRealReportRow above already keeps out of realReports/current, and any
  // future/unexpected value) renders exactly like a legacy row — read-only,
  // no action — never as "issued" (owner-visible rows must never claim
  // publication they never had).
  const mode = !current
    ? (coveredByCustom ? "covered" : "confirm")
    : !current.reportStatus
      ? "legacy"
      : current.reportStatus === "Draft"
        ? "draft"
        : current.reportStatus === "Approved"
          ? "approved"
          : current.reportStatus === "Issued"
            ? "issued"
            : "legacy";
  return {
    code,
    weekStart,
    period,
    // Deliberate rule (spec section 4.5): while a custom Draft or Approved
    // report covers the week, "Confirm data complete" stays hidden and this
    // line says which report covers it.
    coveredBy: coveredByCustom
      ? {
          reportNumber: coveredByCustom.reportNumber,
          label: `Covered by ${reportRef(coveredByCustom.reportStatus, coveredByCustom.publishedNumber)}`,
          coversTag: `Covers ${formatCoversSpan(coveredByCustom.periodStart, coveredByCustom.periodEnd)}`,
          reportStatus: coveredByCustom.reportStatus || "",
        }
      : null,
    weekLabel: period ? formatPeriodLabel(period.periodStart, period.periodEnd) : reportWeekLabel(weekStart, currentYear),
    checklist: shapeReadinessChecklist(readiness),
    reminders: shapeReportReminders(readiness),
    unapprovedRecords: Number(readiness && readiness.unapprovedRecords) || 0,
    allGreen,
    readinessHash: (readiness && readiness.readinessHash) || "",
    mode,
    confirmHelperText: allGreen ? CONFIRM_HELPER_READY : CONFIRM_HELPER_BLOCKED,
    current: current
      ? {
          reportNumber: current.reportNumber,
          publishedNumber: normalisePublishedNumber(current.publishedNumber),
          generationCount: current.generationCount || 0,
          etag: current.etag,
          reportStatus: current.reportStatus,
          dataConfirmedAtUtc: current.dataConfirmedAtUtc || "",
          dataConfirmedByName: current.dataConfirmedBy ? initialsName(current.dataConfirmedBy) : "",
          dataConfirmedLabel: current.dataConfirmedAtUtc ? formatDayTime(current.dataConfirmedAtUtc) : "",
          generatedAtUtc: (latestReportVersion(current) || {}).createdAtUtc || "",
          generatedLabel: (latestReportVersion(current) || {}).createdAtUtc ? formatDayTime(latestReportVersion(current).createdAtUtc) : "",
          dataConfirmedBy: current.dataConfirmedBy || "",
          approvedAtUtc: current.approvedAtUtc || "",
          approvedBy: current.approvedBy || "",
          issuedAtUtc: current.issuedAtUtc || "",
          publishedUrl: current.publishedUrl || "",
        }
      : null,
    archive: realReports.map((report) => shapeReportArchiveRow(report, currentYear)),
    // v2 (DEC-CHAT-20261010-403): the same row, shaped for the table, that the
    // steps read their dates, links and version from.
    currentRow: current ? shapeReportArchiveRow(current, currentYear) : null,
    periodText: period
      ? formatRangeText(period.periodStart, period.periodEnd, { year: true })
      : `Week ${isoWeekNumber(weekStart)} \u00b7 ${weekRangeText(weekStart, { year: true })}`,
    nextReportNumber: realReports.reduce((m, r) => Math.max(m, Number(r.reportNumber) || 0), 0) + 1,
    ...shapeGenerationInProgress(generationInProgress, { weekStart, period }),
    // SPEC-CIS-REPORT-SCHEDULED-PUBLISH-001 section 6: the Approve dialog's
    // Schedule default (absent while the server offers no schedule).
    ...(scheduleDefault && scheduleDefault.publishAtUtc ? { scheduleDefault } : {}),
  };
}

/**
 * The backend's running-generation signal (CisCockpitReports
 * `generationInProgress`, from the one per-project generation claim), split by
 * whether it is for the week/period on screen: `generating` drives this
 * view's progress panel; `generationElsewhere` (another week or period of the
 * same project) only blocks starting another, since the backend would refuse it.
 */
export function shapeGenerationInProgress(raw, { weekStart, period } = {}) {
  if (!raw || !raw.jobId) return { generating: null, generationElsewhere: null };
  const gen = {
    jobId: raw.jobId,
    startedAt: raw.startedAt || "",
    by: raw.by || "",
    state: raw.state || "running",
    reportNumber: raw.reportNumber || 0,
    weekStart: raw.weekStart || "",
    periodStart: raw.periodStart || "",
    periodEnd: raw.periodEnd || "",
  };
  const forThisView = period
    ? gen.periodStart === period.periodStart && gen.periodEnd === period.periodEnd
    : !gen.periodStart && gen.weekStart === weekStart;
  return forThisView ? { generating: gen, generationElsewhere: null } : { generating: null, generationElsewhere: gen };
}

/** The Monday on/before `dateStr` (ISO date string), UTC. */
export function mondayOf(dateStr) {
  const parsed = Date.parse(`${dateStr}T00:00:00Z`);
  if (!Number.isFinite(parsed)) return dateStr;
  const d = new Date(parsed);
  const offset = (d.getUTCDay() + 6) % 7; // Mon=0 .. Sun=6
  d.setUTCDate(d.getUTCDate() - offset);
  return d.toISOString().slice(0, 10);
}

/** This week's Monday, UTC — the Report screen's own "current week" reference. */
export function currentWeekStartUtc(now = new Date()) {
  return mondayOf(now.toISOString().slice(0, 10));
}

/**
 * Report redesign (owner-approved mockup, DEC-CHAT-20261007-412): the week
 * navigation header — "Week N · this week" + the Mon-Sun date range, prev/
 * next button labels, and whether this IS the current week (the "next"
 * button is disabled there — the report pipeline never runs ahead of the
 * real calendar week).
 */
export function reportWeekHeader(weekStart, currentWeekStart = currentWeekStartUtc()) {
  if (!weekStart) return null;
  const n = isoWeekNumber(weekStart);
  const atCurrent = weekStart === currentWeekStart;
  const start = Date.parse(`${weekStart}T00:00:00Z`);
  const end = start + 6 * DAY_MS;
  const fmt = (ms) => {
    const d = new Date(ms);
    return `${d.getUTCDate()} ${MONTH[d.getUTCMonth()]}`;
  };
  return {
    weekNo: n,
    title: `Week ${n}${atCurrent ? " · this week" : ""}`,
    range: Number.isFinite(start) ? `Mon ${fmt(start)} – Sun ${fmt(end)}` : "",
    atCurrent,
    // v2 header text: "Week 41 · 5 – 11 Oct 2026".
    text: `Week ${n} \u00b7 ${weekRangeText(weekStart, { year: true })}`,
    prevLabel: `‹ W${n - 1}`,
    nextLabel: `W${n + 1} ›`,
  };
}

/**
 * The next-week target App.jsx's `onNextWeek` handler should actually
 * request — `candidateNextWeek` (one week ahead of whatever is currently
 * requested) unless that would be PAST the current week, in which case the
 * week stays where it is. Checked against the week being REQUESTED, never
 * the last-loaded model (codex review finding, Report.jsx:375 — a burst of
 * rapid clicks could previously outrun the fetch and request a week past
 * the current one, since the only boundary check lived in the lagging
 * loaded model's own `atCurrent` flag).
 */
export function clampReportWeekForward(candidateNextWeek, currentWeekStart) {
  return candidateNextWeek > currentWeekStart ? null : candidateNextWeek;
}

/**
 * Whole Report screen model from the `GET /api/cockpit/reports?weekStart=`
 * envelope. With `periodKind: "custom"` (SPEC-CIS-REPORT-CUSTOM-PERIOD-001)
 * the header names the period instead of an ISO week and `period` carries it;
 * `periodSupported` is false against a backend that does not return
 * `periodKind` yet, so the screen hides the Custom period control rather than
 * offering something an old backend cannot do.
 */
export function shapeReportPage(envelope, currentYear = new Date().getUTCFullYear(), currentWeekStart = currentWeekStartUtc()) {
  const projects = (envelope && envelope.projects) || [];
  const errors = (envelope && envelope.errors) || [];
  const custom = Boolean(envelope && envelope.periodKind === "custom" && envelope.periodStart && envelope.periodEnd);
  const period = custom ? { periodStart: envelope.periodStart, periodEnd: envelope.periodEnd } : null;
  const header = custom
    ? {
        custom: true,
        title: "Custom period",
        range: formatPeriodLabel(period.periodStart, period.periodEnd),
        text: formatRangeText(period.periodStart, period.periodEnd, { year: true }),
        spanText: periodSpanText(period.periodStart, period.periodEnd),
        atCurrent: false,
        prevLabel: "",
        nextLabel: "",
      }
    : reportWeekHeader(envelope && envelope.weekStart, currentWeekStart);
  return {
    weekStart: envelope && envelope.weekStart,
    period,
    periodSupported: projects.length > 0 && projects.every((p) => p && p.readiness && typeof p.readiness.periodKind === "string"),
    header,
    projects: projects.map((project) => shapeReportProject(project, currentYear)),
    errors,
  };
}

/**
 * The `POST /api/projects/{code}/reports/weekly` body (§4.6 gate). The
 * `readinessHash` is always the exact value the server's own
 * `GET /api/cockpit/reports` response carried for this week — this function
 * never recomputes it; the server recomputes and compares (422
 * `data_incomplete` on any mismatch).
 */
export function buildReportConfirmBody({ weekStart, readinessHash, regenerateReason, prose, period, async: queued }) {
  const body = { weekStart, dataConfirmation: { weekStart, readinessHash } };
  // Weekly report v2: generation takes 90-150 s, longer than the request
  // timeout, so the screen asks for a queued job (202 + jobId) and follows it.
  if (queued === true) body.async = true;
  // SPEC-CIS-REPORT-CUSTOM-PERIOD-001 section 4.2: the period on screen. The
  // server stores a custom report's periodStart in WeekStart, so `weekStart`
  // is the period start and the supplied readiness is the period's.
  if (period && period.periodStart && period.periodEnd) {
    body.weekStart = period.periodStart;
    body.periodStart = period.periodStart;
    body.periodEnd = period.periodEnd;
    body.dataConfirmation = { weekStart: period.periodStart, readinessHash };
  }
  if (regenerateReason) body.regenerateReason = regenerateReason;
  // DEC-CHAT-20260928-202: omitted/true is today's behaviour unchanged
  // (CisReportWeekly defaults `prose` to true); only send the field when the
  // site manager declined the written summary.
  if (prose === false) body.prose = false;
  return body;
}

/**
 * Folds the row a PATCH answered (the server's own read-back of the report it
 * just changed, `pdf` summary aside) into the report page on screen, so the
 * screen shows the new state at once and even when re-reading the page is
 * refused (a custom period that now overlaps an approved report answers 409
 * period_overlap). Only the named report of the named project changes.
 */
export function applyRowToEnvelope(envelope, code, row) {
  if (!envelope || !row || !Number.isFinite(Number(row.reportNumber)) || !Array.isArray(envelope.projects)) return envelope;
  const { pdf, ...fields } = row; // eslint-disable-line no-unused-vars
  return {
    ...envelope,
    projects: envelope.projects.map((project) =>
      project.code !== code
        ? project
        : { ...project, reports: (project.reports || []).map((r) => (r.reportNumber === Number(row.reportNumber) ? { ...r, ...fields } : r)) }
    ),
  };
}

/** The `{ etag, action, reason? }` body for `PATCH /api/projects/{code}/reports/weekly/{reportNumber}` — approve | reopen | publish (§4.6). */
export function buildReportPatchBody({ etag, action, reason }) {
  const body = { etag, action };
  if (reason !== undefined) body.reason = reason;
  return body;
}

/**
 * ---------------------------------------------------------------------------
 * Today by the yard's day (Day redesign phase 1, DEC-CHAT-20261013-201).
 *
 * THE ONE PLACE the backend `day` payload is mapped to the screen. Expected
 * per-project `day` on GET /api/cockpit/today (azure-functions/cis/shared/
 * cockpit.js):
 *   { date, lines: [{ key, plannedTime, timeSlot, title, code, category,
 *       inspectionType, place, block,
 *       sides: [{ side: "P"|"S"|"", scheduleRef, etag, assignedTo|null,
 *                 state: "recorded"|"to_go"|"postponed", result?, inspectionNumber? }],
 *       repairCheck: { openDefects: [{ side?, text, inspectionDate, inspectionNumber? }] } | null }],
 *     counts: { requested, recorded, toGo, postponed, unscheduled },
 *     carried: [{ scheduleRef, title, code, plannedDate, plannedTime, place, block, side, assignedTo }],
 *     lastDailyImport: { at, file, localTime? } | null,   // localTime "07:42" if the server knows the yard's clock; else the browser's clock
 *     expectedCount?: number }   // optional: "about N from the block schedule"
 * Counts are ONE PER SIDE (D8); `unscheduled` (records with no yard request) is
 * a separate figure, never part of `requested` (C7).
 * ---------------------------------------------------------------------------
 */

const SIDE_NAME = { P: "P", S: "S", "": "—" };

/** "08:30–09:00" or "08:30" or "" for a line. */
export function dayLineTime(line) {
  return String(line.timeSlot || line.plannedTime || "").replace(/-/g, "–");
}

function timeSortKey(text) {
  const m = /(\d{1,2}):(\d{2})/.exec(text || "");
  return m ? Number(m[1]) * 60 + Number(m[2]) : 99999;
}

function lowerUpn(value) {
  return String(value || "").trim().toLowerCase();
}

/** "27 Oct" (defects line on the desktop) from an ISO date. */
function shortDate(iso) {
  return formatDayShort(String(iso || "").slice(0, 10));
}

/** "Tue 20 Oct" (defects line on the phone). */
function longDate(iso) {
  return formatDay(`${String(iso || "").slice(0, 10)}T00:00:00Z`);
}

function defectSummary(repairCheck) {
  const defects = (repairCheck && repairCheck.openDefects) || [];
  if (!defects.length) return null;
  const latest = defects.map((d) => String(d.inspectionDate || "").slice(0, 10)).sort().pop();
  const n = defects.length;
  return {
    count: n,
    texts: defects.map((d) => d.text),
    desktopText: `${n} open defect${n === 1 ? "" : "s"} from our inspection on ${shortDate(latest)}`,
    phoneText: `${n} open defect${n === 1 ? "" : "s"} from our inspection on ${longDate(latest)}`,
  };
}

/**
 * The lane a line sits in: "unassigned" when any side still to do has nobody;
 * otherwise "mine" when its open sides are the signed-in site manager's, else
 * "theirs" (the assistant). A fully recorded line follows its first side.
 */
export function dayLineLane(line, me) {
  const open = line.sides.filter((s) => s.state !== "recorded" && s.state !== "carried" && s.state !== "not_attended");
  const basis = open.length ? open : line.sides;
  if (open.length && basis.some((s) => !lowerUpn(s.assignedTo))) return "unassigned";
  const first = lowerUpn(basis[0] && basis[0].assignedTo);
  if (!first) return "unassigned";
  return first === lowerUpn(me) ? "mine" : "theirs";
}

export function shapeDayLine(rawLine, day, me, projectCode = "") {
  const sides = (rawLine.sides || []).map((s) => {
    const side = s.side === "P" || s.side === "S" ? s.side : "";
    const done = s.state === "recorded";
    // Day close (phase 2): a side the site manager carried or marked not attended is closed.
    const carried = s.state === "carried";
    const notAttended = s.state === "not_attended";
    const defectsForSide = ((rawLine.repairCheck && rawLine.repairCheck.openDefects) || []).filter((d) => !d.side || d.side === side);
    return {
      side,
      label: SIDE_NAME[side],
      cls: done ? "sd done" : carried || notAttended ? "sd closed" : "sd",
      state: s.state,
      done,
      carried,
      notAttended,
      closed: carried || notAttended,
      carriedTo: s.carriedTo || "",
      carriedFrom: s.carriedFrom || "",
      notAttendedReason: s.notAttendedReason || "",
      postponed: s.state === "postponed",
      scheduleRef: s.scheduleRef,
      etag: s.etag || "",
      assignedTo: lowerUpn(s.assignedTo),
      result: s.result || "",
      inspectionNumber: s.inspectionNumber || null,
      defectText: defectsForSide.map((d) => d.text).join("; "),
    };
  });
  const block = rawLine.block || "";
  const title = rawLine.title || "";
  const item = block && !title.toUpperCase().startsWith(block.toUpperCase()) ? `${block} · ${title}` : title;
  const repair = defectSummary(rawLine.repairCheck);
  const isRepairCheck = Boolean(rawLine.isRepairCheck) || Boolean(repair);
  const doneCount = sides.filter((s) => s.done).length;
  const closedCount = sides.filter((s) => s.closed).length;
  const lane = dayLineLane({ sides: rawLine.sides || [] }, me);
  let chip;
  if (sides.length && doneCount === sides.length) chip = { text: "RECORDED", tone: "ok" };
  else if (sides.some((s) => s.postponed) && !repair) chip = { text: "POSTPONED", tone: "mute" };
  else if (isRepairCheck) chip = { text: "REPAIR CHECK", tone: "acc" };
  else if (closedCount > 0 && doneCount + closedCount === sides.length) chip = { text: sides.some((s) => s.carried) ? "CARRIED" : "NOT ATTENDED", tone: "mute" };
  else if (doneCount > 0) chip = { text: `${doneCount} OF ${sides.length} DONE`, tone: "mute" };
  else chip = { text: "TO GO", tone: "mute" };
  const project = rawLine.projectCode || projectCode || "";
  return {
    key: `${project}|${rawLine.key || `${block}|${title}|${rawLine.plannedTime || ""}`}`,
    projectCode: project, // the PROJECT (from the project envelope); `code` below is the yard stage code
    code: rawLine.code || "",
    stage: rawLine.stage || "",
    notes: rawLine.notes,
    witnessParties: rawLine.witnessParties,
    date: day.date,
    time: dayLineTime(rawLine),
    timeText: dayLineTime(rawLine) || "no time",
    block,
    title,
    item,
    place: rawLine.place || "",
    category: rawLine.category || "Others",
    inspectionType: rawLine.inspectionType || "Witness Point",
    plannedTime: rawLine.plannedTime || "",
    repair: isRepairCheck,
    defectsDesktop: repair ? repair.desktopText : "",
    defectsPhone: repair ? repair.phoneText : "",
    sides,
    open: doneCount + closedCount < sides.length,
    done: sides.length > 0 && doneCount === sides.length,
    requestedSides: sides.length,
    recordedSides: doneCount,
    noSide: sides.length === 1 && sides[0].side === "",
    chip,
    lane,
    cls: sides.length > 0 && doneCount === sides.length ? "r past" : "r",
  };
}

function sumCounts(days) {
  const total = { requested: 0, recorded: 0, toGo: 0, postponed: 0, unscheduled: 0, carriedOver: 0, notAttended: 0 };
  for (const d of days) for (const k of Object.keys(total)) total[k] += Number((d.counts || {})[k]) || 0;
  return total;
}

function clockOf(iso) {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return "";
  const d = new Date(t);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/** Top-bar label for the day the server says it is: "W42 \u00b7 Wed 14 Oct" (same form as the browser-date label). */
export function topBarDayLabel(date) {
  if (!date) return "";
  const d = new Date(`${date}T00:00:00Z`);
  if (!Number.isFinite(d.getTime())) return "";
  return `W${isoWeekNumber(date)} \u00b7 ${WEEKDAY[d.getUTCDay()]} ${d.getUTCDate()} ${MONTH[d.getUTCMonth()]}`;
}

/** "Tue 27 Oct · W44" */
export function dayHeaderLabel(date) {
  if (!date) return "";
  return `${formatDay(`${date}T00:00:00Z`)} · W${isoWeekNumber(date)}`;
}

/**
 * Whole day-view model from the `/api/cockpit/today` envelope (already
 * narrowed to the selected projects). `me` = the signed-in UPN.
 */
export function shapeDayScreen(envelope, { me = "" } = {}) {
  const projects = ((envelope && envelope.projects) || []).filter((p) => p && p.day);
  const days = projects.map((p) => p.day);
  const date = (days[0] && days[0].date) || "";
  const lines = [];
  for (const p of projects) {
    for (const raw of p.day.lines || []) lines.push(shapeDayLine(raw, p.day, me, p.code));
  }
  lines.sort((a, b) => timeSortKey(a.time) - timeSortKey(b.time) || a.item.localeCompare(b.item));
  const counts = days.length ? sumCounts(days) : { requested: 0, recorded: 0, toGo: 0, postponed: 0, unscheduled: 0, carriedOver: 0, notAttended: 0 };
  const laneBlock = (lane) => {
    const rows = lines.filter((l) => l.lane === lane);
    return { lines: rows, sides: rows.reduce((n, l) => n + l.requestedSides, 0), recorded: rows.reduce((n, l) => n + l.recordedSides, 0) };
  };
  const carried = [];
  for (const p of projects) {
    for (const c of p.day.carried || []) {
      carried.push({
        key: c.scheduleRef,
        projectCode: c.projectCode || p.code,
        text: [c.block, c.title].filter(Boolean).join(" · ") + (c.side ? ` · ${c.side}` : ""),
      });
    }
  }
  const imports = days.map((d) => d.lastDailyImport).filter(Boolean).sort((a, b) => String(b.at).localeCompare(String(a.at)));
  const last = imports[0] || null;
  const expected = days.reduce((n, d) => n + (Number(d.expectedCount) || 0), 0);
  const pct = counts.requested > 0 ? Math.round((counts.recorded / counts.requested) * 100) : 0;
  return {
    date,
    dateLabel: dayHeaderLabel(date),
    hasList: lines.length > 0,
    // End of day (phase 2) is offered when the day has sides to close or records to approve.
    toApproveCount: days.reduce((n, d) => n + ((d.toApprove || []).length), 0),
    canEndDay: lines.length > 0 || days.some((d) => (d.toApprove || []).length > 0),
    counts,
    requestedText: `${counts.requested} requested`,
    recordedText: `${counts.recorded} recorded`,
    toGoText: `${counts.toGo} to go`,
    postponedText: counts.postponed ? `${counts.postponed} postponed` : "",
    closedText: [counts.carriedOver ? `${counts.carriedOver} carried` : "", counts.notAttended ? `${counts.notAttended} not attended` : ""].filter(Boolean).join(" · "),
    pct: `${pct}%`,
    importedAt: last ? last.localTime || clockOf(last.at) : "",
    importText: last ? `Yard's daily list uploaded ${last.localTime || clockOf(last.at)}` : "No yard daily list uploaded yet",
    unscheduledText: `Also recorded without a yard request: ${counts.unscheduled}`,
    unassigned: laneBlock("unassigned"),
    mine: laneBlock("mine"),
    theirs: laneBlock("theirs"),
    carried,
    expectedCount: expected,
    projectCodes: projects.map((p) => p.code),
    multiProject: new Set(lines.map((l) => l.projectCode)).size > 1,
    lineCount: lines.length,
  };
}

/**
 * The people a line can be assigned to, from Plan's shaped lanes (the roster):
 * the signed-in site manager's own lane, and the other inspectors on the
 * project's roster (the bare roster entries). A `lane.sm === true` hint, if the
 * backend ever sends one, also marks the site manager.
 */
export function dayPeopleFor(code, lanes, me) {
  const onRoster = (lanes || []).filter((l) => l.upn && (l.rosterCodes || l.teams || []).includes(code));
  const meKey = lowerUpn(me);
  const sm = onRoster.find((l) => lowerUpn(l.upn) === meKey) || null;
  const assistants = onRoster.filter((l) => l !== sm && !l.sm).map((l) => ({ upn: lowerUpn(l.upn), name: l.name || initialsName(l.upn) }));
  return { sm: sm ? { upn: lowerUpn(sm.upn), name: sm.name || initialsName(sm.upn) } : null, assistants };
}

/**
 * The upsert items that assign a line to `assignedTo` ("" = unassigned): one
 * per side still to do (a recorded side keeps its person). Each item is
 * EXACTLY { scheduleRef, etag, assignedTo } (backend assign-only upsert): no
 * stage, notes, place, block, side or time is ever sent, so an assignment
 * cannot backfill or rewrite an old row. Returns { ok, items, missing }:
 * `missing` lists sides that carry no etag, and then nothing is sent.
 */
export function buildDayAssignItems(line, assignedTo) {
  const items = [];
  const missing = [];
  for (const s of line.sides.filter((x) => !x.done && !x.closed)) {
    if (!s.etag) missing.push(s.scheduleRef);
    items.push({ scheduleRef: s.scheduleRef, etag: s.etag, assignedTo: assignedTo || "" });
  }
  return { ok: missing.length === 0, items, missing };
}

const BLOCK_PATTERN = /^[A-Z0-9]+(?:-[A-Z])?$/i;

/**
 * "Add one inspection" form -> the schedule upsert items (new optional fields
 * place / block / side / plannedTime). Ref "MAN-<yyyymmdd>-<8 hex>"; side "PS"
 * (P and S) makes two items, `<ref>-P` and `<ref>-S` (D8: one row per side).
 * Category (one of CIS Capture's eight) and stage (from that category's list,
 * or the yard's own wording via "Other") are required, as in CIS Capture's
 * New record, so the booking prefills the capture form.
 */
export function buildAddInspectionItem(values, { date, uuid } = {}) {
  const v = values || {};
  const text = (x) => (typeof x === "string" ? x.trim() : "");
  const errors = {};
  const item = text(v.item);
  if (!item) errors.item = "Enter what is being inspected";
  else if (item.length > 255) errors.item = "At most 255 characters";
  const time = text(v.time);
  if (time && !/^([01]\d|2[0-3]):[0-5]\d$/.test(time)) errors.time = "Use a time like 09:30";
  const side = text(v.side).toUpperCase();
  if (!["", "P", "S", "PS"].includes(side)) errors.side = "Choose P, S, P and S, or no side";
  const place = text(v.place);
  if (place.length > 255) errors.place = "At most 255 characters";
  const block = text(v.block).toUpperCase();
  if (block && (block.length > 32 || !BLOCK_PATTERN.test(block))) errors.block = "A block code such as UD10 or BS2-D";
  const category = text(v.category);
  if (!CATEGORIES.includes(category)) errors.category = "Choose a category";
  const stage = text(v.stage);
  if (!stage) errors.stage = category ? "Choose a stage" : "Choose a category first";
  else if (stage.length > 255) errors.stage = "At most 255 characters";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date || "")) errors.date = "No day";
  const code = text(v.code);
  if (!code) errors.code = "Choose a project";
  if (Object.keys(errors).length) return { ok: false, errors };
  const hex8 = (uuid || cryptoRandomUuidV4()).replace(/-/g, "").slice(0, 8);
  const ref = `MAN-${date.replace(/-/g, "")}-${hex8}`;
  const base = {
    title: item,
    category,
    stage,
    inspectionType: "Witness Point",
    plannedDate: date,
    witnessParties: "",
    assignedTo: "",
    notes: "",
  };
  if (time) base.plannedTime = time;
  if (place) base.place = place;
  if (block) base.block = block;
  const items = side === "PS"
    ? ["P", "S"].map((sd) => ({ scheduleRef: `${ref}-${sd}`, ...base, side: sd }))
    : [{ scheduleRef: ref, ...base, ...(side ? { side } : {}) }];
  return { ok: true, code, items, item: items[0] };
}

/** The file picker filter for a daily list: extensions plus the odd types phones give a saved WeChat file. */
export const IMPORT_FILE_ACCEPT =
  ".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,application/octet-stream,application/x-zip-compressed,application/zip";

/**
 * Pending local changes laid over the Today envelope until the server read-back
 * replaces them (and for the fixture world, which has no server): `assigned`
 * maps scheduleRef -> assignedTo, `added` lists { code (project), items } rows created by
 * "Add one inspection". Pure; never mutates `envelope`.
 */
export function applyDayOverrides(envelope, { assigned = {}, added = [] } = {}) {
  if (!envelope || (!Object.keys(assigned).length && !added.length)) return envelope;
  return {
    ...envelope,
    projects: (envelope.projects || []).map((p) => {
      if (!p.day) return p;
      const lines = (p.day.lines || []).map((line) => ({
        ...line,
        sides: (line.sides || []).map((s) => (Object.prototype.hasOwnProperty.call(assigned, s.scheduleRef) ? { ...s, assignedTo: assigned[s.scheduleRef] || null } : s)),
      }));
      const mine = added.filter((a) => a.code === p.code);
      let sideCount = 0;
      for (const a of mine) {
        const first = a.items[0];
        sideCount += a.items.length;
        lines.push({
          key: first.scheduleRef,
          plannedTime: first.plannedTime || "",
          timeSlot: "",
          title: first.title,
          code: "",
          category: first.category,
          stage: first.stage,
          inspectionType: first.inspectionType,
          place: first.place || "",
          block: first.block || "",
          sides: a.items.map((it) => ({ side: it.side || "", scheduleRef: it.scheduleRef, etag: "", assignedTo: null, state: "to_go" })),
          repairCheck: null,
        });
      }
      const counts = { ...p.day.counts };
      counts.requested = (counts.requested || 0) + sideCount;
      counts.toGo = (counts.toGo || 0) + sideCount;
      return { ...p, day: { ...p.day, lines, counts } };
    }),
  };
}

/**
 * The daily-list upload preview, one row per LINE. The server previews one
 * candidate per side with the key `<ref>` or `<ref>-P` / `<ref>-S` (D8) and the
 * fields time, place, block, side. Rows are grouped by that key stem ONLY: a
 * "P" and an "S" of one booking share a row; two different bookings never do,
 * whatever their text. Returns
 * { lines: [{ key, time, title, place, block, sides, sideLabel, status }], sides, date }.
 */
export function groupDailyCandidates(rows) {
  const lines = [];
  const index = new Map();
  let sides = 0;
  let date = "";
  for (const row of rows || []) {
    const key = String(row.key || "");
    const keySide = /-([PS])$/.exec(key);
    const stem = keySide ? key.slice(0, keySide.index) : key;
    const label = String(row.label || "");
    const labelSide = / \u00b7 ([PS])$/.exec(label);
    const side = row.side === "P" || row.side === "S" ? row.side : keySide ? keySide[1] : "";
    const title = labelSide ? label.slice(0, labelSide.index) : label;
    if (!date && row.date) date = row.date;
    sides += 1;
    let line = index.get(stem);
    if (!line) {
      const shown = row.block && !title.toUpperCase().startsWith(String(row.block).toUpperCase()) ? `${row.block} \u00b7 ${title}` : title;
      line = { key: stem, time: String(row.time || "").replace(/-/g, "\u2013"), title: shown, place: row.place || "", block: row.block || "", sides: [], status: row.status };
      index.set(stem, line);
      lines.push(line);
    }
    if (side && !line.sides.includes(side)) line.sides.push(side);
    if (row.status !== "new" && row.status !== "unchanged") line.status = row.status;
  }
  for (const line of lines) {
    line.sides.sort();
    line.sideLabel = line.sides.length === 2 ? "P + S" : line.sides.length === 1 ? line.sides[0] : "NO SIDE";
  }
  return { lines, sides, date };
}
